import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { roundParPayload, isTotalScoreSaveDisabled } from "../utils/scorePayload";
import { toast } from "sonner";
import { useFullScreen } from "./DarkAppShell";
import { 
  Calendar as CalendarIcon,
  Camera,
  Upload,
  X,
  Check,
  PlusCircle,
  Zap,
  Trash2,
  Mic,
  MicOff,
  Volume2,
  ChevronDown,
  Search,
  FileText,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Image as ImageIcon,
  Share2,
  ChevronLeft,
  ChevronRight,
  Flag,
  MapPin,
  Target
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "./ui/dialog";
import { format } from "date-fns";
import ScoreEntryForm from "./ScoreEntryForm";
import { useScoreAnimationPreference } from '../hooks/useScoreAnimationPreference';
import { warmScoreReactions } from './score-reactions/preload';
import ScoreSetupCarousel from "./ScoreSetupCarousel";
import { useResumeScoreDraft } from '../hooks/useResumeScoreDraft';
import { createScoreDraftId, scoreDraftPath } from '../utils/scoreDraft';
import SharedHoleInfoCard from "./SharedHoleInfoCard";
import soundEffects from "../utils/soundEffects";
import { ShareableScorecard } from "./ShareableScorecard";

// Voice Entry Sub-Components (refactored)
import { VoiceScoreGrid, CourseLookupUI } from "./voice";

// Score Entry Sub-Components (refactored Phase 2)
import { HolesSelector, TotalScoreEntry, ScannerUI, PlayModeSelector } from "./score-entry";

// Group Round Components
import GroupRoundSetup from "./GroupRoundSetup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ COURSE CONFIRM CARD COMPONENT ============
// Swipeable card that appears after course is found - user swipes to confirm
const CourseConfirmCard = ({ 
  course, 
  onConfirm, 
  onReject,
  isVisible 
}) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [exitDirection, setExitDirection] = useState(null);
  const audioContextRef = useRef(null);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(80, audioContext.currentTime + 0.15);
      
      gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.15);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {}
  }, []);

  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.touches[0].clientX - dragStart);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    const threshold = 100;
    if (dragOffset > threshold) {
      // Swipe right = confirm
      playSwoosh();
      setExitDirection('right');
      setIsExiting(true);
      setTimeout(() => onConfirm(), 300);
    } else if (dragOffset < -threshold) {
      // Swipe left = reject
      playSwoosh();
      setExitDirection('left');
      setIsExiting(true);
      setTimeout(() => onReject(), 300);
    } else {
      setDragOffset(0);
    }
    setIsDragging(false);
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - dragStart);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const threshold = 100;
    if (dragOffset > threshold) {
      playSwoosh();
      setExitDirection('right');
      setIsExiting(true);
      setTimeout(() => onConfirm(), 300);
    } else if (dragOffset < -threshold) {
      playSwoosh();
      setExitDirection('left');
      setIsExiting(true);
      setTimeout(() => onReject(), 300);
    } else {
      setDragOffset(0);
    }
    setIsDragging(false);
  };

  const getTransform = () => {
    if (isExiting) {
      return exitDirection === 'right' ? 'translateX(120%)' : 'translateX(-120%)';
    }
    return `translateX(${dragOffset}px) rotate(${dragOffset * 0.02}deg)`;
  };

  const getOverlayColor = () => {
    if (dragOffset > 50) return 'rgba(34, 197, 94, 0.3)'; // Green for confirm
    if (dragOffset < -50) return 'rgba(239, 68, 68, 0.3)'; // Red for reject
    return 'transparent';
  };

  if (!isVisible || !course) return null;

  const cardContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      style={{ touchAction: 'pan-y' }}
    >
      <div
        className={`
          w-[90%] max-w-sm select-none
          transition-all duration-300 ease-out
          ${isDragging ? 'transition-none' : ''}
          ${isExiting ? 'opacity-0' : 'opacity-100'}
        `}
        style={{
          transform: getTransform(),
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Swipe indicator overlays */}
        <div 
          className="absolute inset-0 rounded-3xl transition-colors duration-200 pointer-events-none"
          style={{ backgroundColor: getOverlayColor() }}
        />
        
        {/* Card with green golf branding */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, var(--ps-bg-elevated), var(--ps-bg-surface))',
            boxShadow: '0 0 60px rgba(34, 197, 94, 0.4), 0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Top decorative bar */}
          <div className="h-2 bg-gradient-to-r from-green-400 via-emerald-300 to-green-400" />
          
          {/* Flag icon decoration */}
          <div className="absolute top-6 right-6 opacity-20">
            <Flag className="w-16 h-16 text-green-300" />
          </div>

          <div className="p-6 relative">
            {/* Course Found Badge */}
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 backdrop-blur flex items-center justify-center border-2 border-green-400/50">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </div>

            {/* Course Name */}
            <div className="text-center mb-4">
              <h3 className="text-2xl font-black text-white mb-1">{course.name}</h3>
              {course.location && (
                <p className="text-green-300/80 text-sm flex items-center justify-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {course.location}
                </p>
              )}
            </div>

            {/* Course Info Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Par */}
              <div className="bg-black/30 rounded-xl p-3 text-center">
                <Target className="w-5 h-5 mx-auto mb-1 text-green-400" />
                <div className="text-xs text-green-300/70 font-medium">PAR</div>
                <div className="text-3xl font-black text-green-400">{course.par || 72}</div>
              </div>
              
              {/* Holes */}
              <div className="bg-black/30 rounded-xl p-3 text-center">
                <Flag className="w-5 h-5 mx-auto mb-1 text-blue-400" />
                <div className="text-xs text-blue-300/70 font-medium">HOLES</div>
                <div className="text-3xl font-black text-blue-400">{course.hole_pars?.length || 18}</div>
              </div>
            </div>

            {/* Swipe instructions */}
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-1 text-red-400/70">
                <ChevronLeft className="w-5 h-5" />
                <span>Not Right</span>
              </div>
              <div className="flex items-center gap-1 text-green-400/70">
                <span>That's It!</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Bottom decorative bar */}
          <div className="h-1 bg-gradient-to-r from-transparent via-green-400/50 to-transparent" />
        </div>
      </div>
    </div>
  );
  
  return createPortal(cardContent, document.body);
};

const AddScorePage = ({ refreshSubscription, user, refreshProfile }) => {
  const scoreAnimationPreference = useScoreAnimationPreference(user?.user_id);
  useEffect(() => { if (scoreAnimationPreference.enabled) warmScoreReactions(); }, [scoreAnimationPreference.enabled]);
  const navigate = useNavigate();
  const location = useLocation();
  const resumeParams = new URLSearchParams(location.search);
  const requestedDraftId = resumeParams.get('draft');
  const resumingDraft = resumeParams.get('resume') === 'solo' || Boolean(location.state?.continueSoloRound);
  const [draftId, setDraftId] = useState(() => requestedDraftId || createScoreDraftId());
  const { setIsFullScreen } = useFullScreen();
  const [playMode, setPlayMode] = useState(null); // null (selecting), 'solo', 'group', 'skins', 'matchplay'
  const [showPlayModeSelector, setShowPlayModeSelector] = useState(false); // Show after course selection
  const [requireCourseFirst, setRequireCourseFirst] = useState(true); // Force course selection on page load
  
  // Manual entry state
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(new Date());
  const [selectedCourse, setSelectedCourse] = useState(location.state?.top10Course?.course_id || null);
  const [front9, setFront9] = useState('');
  const [back9, setBack9] = useState('');
  const [notes, setNotes] = useState('');
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [addCourseMode, setAddCourseMode] = useState('choose'); // 'choose', 'scan', 'manual'
  const [scanningCourse, setScanningCourse] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [newCoursePar, setNewCoursePar] = useState('72');
  const [newCourseLocation, setNewCourseLocation] = useState('');
  const [scannedHolePars, setScannedHolePars] = useState(Array(18).fill(''));
  const courseCameraInputRef = useRef(null);
  const courseGalleryInputRef = useRef(null);
  const [manualEntryMode, setManualEntryMode] = useState('hole'); // 'total' or 'hole'
  const [holeEntryMethod, setHoleEntryMethod] = useState('manual'); // 'voice', 'manual', or 'scan'
  const [manualHoleScores, setManualHoleScores] = useState(Array(18).fill(''));
  const [scannedHoleScoresForTotal, setScannedHoleScoresForTotal] = useState([]); // Store scanned hole scores when using Total mode
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [numberOfHoles, setNumberOfHoles] = useState(18); // 9 or 18
  const [courseMenuExpanded, setCourseMenuExpanded] = useState(true); // Expanded by default to prompt course selection first

  // Group Round state
  const [showGroupRoundSetup, setShowGroupRoundSetup] = useState(false);
  const [groupRoundData, setGroupRoundData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Scanner state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [editedHoles, setEditedHoles] = useState([]);
  const [courseName, setCourseName] = useState('');
  const [roundDate, setRoundDate] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verifiedScores, setVerifiedScores] = useState(false);

  // Voice entry state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [currentVoiceHole, setCurrentVoiceHole] = useState(1);
  const [voiceScores, setVoiceScores] = useState(Array(18).fill(null));
  const [audioFeedbackEnabled, setAudioFeedbackEnabled] = useState(true);
  const [voiceIntroPlayed, setVoiceIntroPlayed] = useState(false);
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('shimmer'); // shimmer=Miss Birdie, onyx=Mr. Birdie
  const [isListeningForWakeWord, setIsListeningForWakeWord] = useState(false);
  const [voiceMode, setVoiceMode] = useState('idle'); // idle, listening, menu, hole_by_hole, quick
  const [wakeWordDetected, setWakeWordDetected] = useState(false);
  const [courseLookupMode, setCourseLookupMode] = useState(false);
  const [courseLookupStep, setCourseLookupStep] = useState('idle'); // idle, searching, confirm, select_nines, not_found, swipe_confirm
  const [showCourseConfirmCard, setShowCourseConfirmCard] = useState(false); // Swipeable course confirmation card
  const [pendingCourseName, setPendingCourseName] = useState('');
  const [pendingCourseLocation, setPendingCourseLocation] = useState('');
  const [foundCourse, setFoundCourse] = useState(location.state?.top10Course || null);
  const [existingCourseMatch, setExistingCourseMatch] = useState(null); // Existing course that matches lookup
  const [selectedNines, setSelectedNines] = useState(null); // For 27-hole courses: {front: 0, back: 1} (indices into nines array)
  const [startingNine, setStartingNine] = useState('front'); // 'front' or 'back' - which nine to start on for regular 18-hole courses
  const [firstNineSelection, setFirstNineSelection] = useState(null); // For 27-hole UI: index of first nine selected
  const [secondNineSelection, setSecondNineSelection] = useState(null); // For 27-hole UI: index of second nine selected
  const [voiceInputMode, setVoiceInputMode] = useState(null); // null = not chosen, 'interactive' = wake word, 'tap' = tap to record
  const [showVoiceModeChoice, setShowVoiceModeChoice] = useState(false);
  
  // Tee selection state for yardages
  const [availableTees, setAvailableTees] = useState([]); // Tees from selected course
  const [selectedTee, setSelectedTee] = useState(null); // Selected tee object with hole_yardages
  const [holeYardages, setHoleYardages] = useState([]); // Current hole yardages based on tee selection
  const [holeHandicaps, setHoleHandicaps] = useState([]); // Hole handicap ratings
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechRecognitionRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingAudioRef = useRef(false);
  const isListeningRef = useRef(false); // Track listening state for closure
  const welcomePlayedRef = useRef(false); // Strict guard against double welcome
  const welcomeAudioRef = useRef(null); // Reference to welcome audio for cleanup
  const currentAudioRef = useRef(null); // Track any currently playing audio for stopping
  const topSectionRef = useRef(null); // Ref for scrolling to top

  // Share scorecard state
  const [showShareScorecard, setShowShareScorecard] = useState(false);
  const [savedScoreData, setSavedScoreData] = useState(null);
  const [showPostSaveOptions, setShowPostSaveOptions] = useState(false);
  const [latestHandicap, setLatestHandicap] = useState(user?.handicap);
  
  // Setup mode - show carousel first, then clean scorecard
  const [setupComplete, setSetupComplete] = useState(false);
  const [showSetupCarousel, setShowSetupCarousel] = useState(!resumingDraft);
  const resumeDraft = useResumeScoreDraft({ enabled: resumingDraft, userId: user?.user_id, draftId: requestedDraftId, onRestore: draft => {
    setDraftId(draft.draftId);
    setManualHoleScores(draft.scores);
    setVoiceScores(draft.scores.map(score => score === '' ? null : Number(score)));
    setSelectedCourse(draft.courseId);
    setFoundCourse(draft.course);
    setNumberOfHoles(draft.holes);
    setStartingNine(draft.startingNine);
    setHoleEntryMethod(draft.entryMethod);
    setSelectedTee(draft.tee);
    setHoleYardages(draft.yardages);
    setHoleHandicaps(draft.handicaps);
    setDate(draft.date);
    setPlayMode('solo');
    setManualEntryMode('hole');
    setShowPlayModeSelector(false);
    setCourseMenuExpanded(false);
    setShowSetupCarousel(false);
    setSetupComplete(true);
    setLastSaved(new Date());
  }});
  
  // Manual hole info card trigger
  const [showManualHoleInfo, setShowManualHoleInfo] = useState(false);
  const [manualHoleInfoNumber, setManualHoleInfoNumber] = useState(1);

  // Helper function to scroll to top of page - ensures 9/18 hole toggle stays visible
  const scrollToTop = () => {
    // Immediate scroll attempt
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Delayed scroll to handle DOM updates and iOS Safari rendering delays
    setTimeout(() => {
      // Force scroll to absolute top first
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    
    // Second attempt for after React re-renders
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 200);
    
    // Third attempt for iOS Safari which sometimes needs more time
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 400);
  };

  // Helper function to get actual course hole number based on starting nine
  // For back-9-first: internal index 0 maps to hole 10, index 9 maps to hole 1
  const getActualHoleNumber = (internalIndex) => {
    if (numberOfHoles === 9) {
      // 9-hole round: just add 1 to get 1-9 or 10-18 based on startingNine
      return startingNine === 'front' ? internalIndex + 1 : internalIndex + 10;
    }
    
    // 18-hole round
    if (startingNine === 'front') {
      // Standard: holes 1-18 in order
      return internalIndex + 1;
    } else {
      // Back first: first 9 are holes 10-18, second 9 are holes 1-9
      if (internalIndex < 9) {
        return internalIndex + 10; // 0-8 => 10-18
      } else {
        return internalIndex - 8; // 9-17 => 1-9
      }
    }
  };

  // Handler for when setup carousel completes
  const handleSetupComplete = async (setupData) => {
    // A new round receives its own recovery record; existing unfinished rounds stay available.
    setDraftId(createScoreDraftId());
    setManualHoleScores(Array(18).fill(''));
    setVoiceScores(Array(18).fill(null));
    
    // Apply all the setup data
    setNumberOfHoles(setupData.numberOfHoles);
    setStartingNine(setupData.startingNine);
    setHoleEntryMethod(setupData.entryMethod);
    setHoleHandicaps(setupData.course?.hole_handicaps || []);
    
    if (setupData.course) {
      setSelectedCourse(setupData.course.id || setupData.course.course_id);
      setFoundCourse(setupData.course);
    } else {
      // Clear course if none selected
      setSelectedCourse(null);
      setFoundCourse(null);
    }
    
    if (setupData.tee) {
      setSelectedTee(setupData.tee);
      setHoleYardages(setupData.holeYardages || []);
    } else {
      setSelectedTee(null);
      setHoleYardages([]);
    }
    
    // Close carousel and show clean scorecard
    setShowSetupCarousel(false);
    setSetupComplete(true);
    setCourseMenuExpanded(false); // Collapse course menu since it's already selected
    
    toast.success(`Ready! ${setupData.numberOfHoles} holes starting ${setupData.startingNine === 'front' ? 'Front 9' : 'Back 9'}`);
  };

  // Full screen mode when in setup carousel or entering scores
  useEffect(() => {
    if (showSetupCarousel || setupComplete) {
      setIsFullScreen(true);
    }
    return () => setIsFullScreen(false);
  }, [showSetupCarousel, setupComplete, setIsFullScreen]);

  // Personal drafts are loaded only by the explicit, non-destructive Resume path.
  useEffect(() => {
    // Mark voice intro as played immediately (instructional voice removed)
    setVoiceIntroPlayed(true);
    welcomePlayedRef.current = true;
    
    // Cleanup: Stop all audio when user navigates away from this page
    return () => {
      // Stop any playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
      // Clear audio queue
      audioQueueRef.current = [];
      isPlayingAudioRef.current = false;
      // Stop speech recognition
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
    };
  }, []);

  // Handle navigation state for startGroupRound or continueSoloRound
  useEffect(() => {
    if (location.state?.startGroupRound) {
      // Check if a course is already selected
      if (selectedCourse && foundCourse) {
        // Course is selected, open Group Round setup
        setShowGroupRoundSetup(true);
      } else {
        // No course selected - show toast and let them select one
        toast.info('Select a golf course to start your group round', {
          duration: 4000,
          icon: '⛳'
        });
        // Scroll to course search
        setTimeout(() => {
          const searchInput = document.querySelector('[data-testid="course-search-input"]');
          if (searchInput) {
            searchInput.focus();
          }
        }, 500);
      }
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
    
  }, [location.state, selectedCourse, foundCourse]);

  // Update currentVoiceHole when startingNine changes
  useEffect(() => {
    // Reset to first hole of the selected nine
    if (startingNine === 'back') {
      setCurrentVoiceHole(10); // Start at hole 10 for Back 9
    } else {
      setCurrentVoiceHole(1); // Start at hole 1 for Front 9
    }
  }, [startingNine]);

  const draftPayload = scores => ({
    draft_id: draftId,
    hole_scores: scores.map(score => score === '' ? null : parseInt(score) || null),
    course_id: selectedCourse, date: date.toISOString(),
    number_of_holes: numberOfHoles, starting_nine: startingNine, entry_method: holeEntryMethod,
    course_name: foundCourse?.club_name || foundCourse?.name || null,
    tee_name: selectedTee?.name || null,
    hole_pars: foundCourse?.hole_pars || [], hole_yardages: holeYardages, hole_handicaps: holeHandicaps,
  });

  // Save draft immediately when score changes
  const saveDraft = async (scores, courseId = selectedCourse) => {
    if (!scores.some(s => s !== '' && s !== null)) return;
    
    setSavingDraft(true);
    try {
      await axios.put(`${API}/scores/draft`, { ...draftPayload(scores), course_id: courseId });
      setLastSaved(new Date());
    } catch (error) {
      console.error("Failed to save draft:", error);
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle manual hole score change with immediate save
  const handleManualHoleChange = (index, value) => {
    const newScores = [...manualHoleScores];
    newScores[index] = value;
    setManualHoleScores(newScores);
    
    // Save immediately
    if (value !== '') {
      saveDraft(newScores);
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API}/courses`);
        const linked = location.state?.top10Course;
        setCourses(linked && !response.data.some(c => c.course_id === linked.course_id) ? [linked, ...response.data] : response.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
    
    // Fetch current user for group rounds
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setCurrentUser(response.data);
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Manual entry handlers
  const handleAddCourse = async () => {
    if (!newCourseName.trim()) {
      toast.error("Please enter a course name");
      return;
    }

    try {
      // Include hole_pars if they were scanned
      const holePars = scannedHolePars.some(p => p) 
        ? scannedHolePars.map(Number)
        : null;
      if (holePars && (holePars.some(p => !Number.isInteger(p) || p < 2 || p > 7) || ![9, 18].includes(holePars.length))) {
        toast.error('Enter the real par for every selected hole');
        return;
      }
      
      const response = await axios.post(`${API}/courses`, {
        name: newCourseName.trim(),
        par: parseInt(newCoursePar),
        location: newCourseLocation.trim() || null,
        hole_pars: holePars
      });
      
      const newCourse = response.data;
      setCourses([...courses, newCourse]);
      
      // Reset dialog state
      setShowNewCourse(false);
      setAddCourseMode('choose');
      setNewCourseName('');
      setNewCoursePar('72');
      setNewCourseLocation('');
      setScannedHolePars(Array(18).fill(''));
      
      toast.success("Course added!");
      
      // Properly trigger play mode selection (same as course lookup flow)
      handleCourseSelected(newCourse.course_id, newCourse);
      
    } catch (error) {
      toast.error("Failed to add course");
    }
  };

  // Handle AI scorecard scan for course
  const handleCourseScan = async (imageFile) => {
    setScanningCourse(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await axios.post(`${API}/ai/scan-course-card`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data) {
        // Extract course info from scan
        if (response.data.course_name) {
          setNewCourseName(response.data.course_name);
        }
        if (response.data.location) {
          setNewCourseLocation(response.data.location);
        }
        if (response.data.hole_pars && response.data.hole_pars.length > 0) {
          setScannedHolePars(response.data.hole_pars);
          const totalPar = response.data.hole_pars.reduce((sum, p) => sum + (parseInt(p) || 0), 0);
          setNewCoursePar(totalPar.toString());
        }
        setAddCourseMode('manual');
        toast.success("Scorecard scanned! Review the details below.");
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error("Failed to scan scorecard. Please enter details manually.");
      setAddCourseMode('manual');
    } finally {
      setScanningCourse(false);
    }
  };

  const handleCourseImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleCourseScan(file);
    }
  };

  // Delete a saved course
  const handleDeleteCourse = async (courseId, courseName) => {
    if (!window.confirm(`Remove "${courseName}" from your saved courses?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/courses/${courseId}`);
      setCourses(courses.filter(c => c.course_id !== courseId));
      if (selectedCourse === courseId) {
        setSelectedCourse('');
      }
      toast.success(`Removed ${courseName}`);
    } catch (error) {
      toast.error("Failed to remove course");
    }
  };

  // Calculate totals based on entry mode and number of holes
  const calculateManualHoleTotals = () => {
    const front9Total = manualHoleScores.slice(0, 9).reduce((sum, s) => sum + (s ? parseInt(s) : 0), 0);
    const back9Total = numberOfHoles === 18 
      ? manualHoleScores.slice(9, 18).reduce((sum, s) => sum + (s ? parseInt(s) : 0), 0)
      : 0;
    
    // Check if all required holes are filled
    const relevantScores = manualHoleScores.slice(0, numberOfHoles);
    const allFilled = relevantScores.every(s => s !== '' && s !== null);
    
    const total = allFilled ? front9Total + back9Total : null;
    return { front9Total, back9Total, total, allFilled };
  };

  const totalScore = manualEntryMode === 'hole' 
    ? calculateManualHoleTotals().total
    : front9 && (numberOfHoles === 9 || back9) ? parseInt(front9) + (numberOfHoles === 18 ? parseInt(back9) : 0) : null;

  const updateManualHoleScore = (index, value) => {
    const newScores = [...manualHoleScores];
    newScores[index] = value;
    setManualHoleScores(newScores);
    
    // Save immediately to backend
    if (value !== '') {
      saveDraft(newScores);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCourse) {
      toast.error("Please select a course");
      return;
    }

    const course = courses.find(c => c.course_id === selectedCourse);
    
    if (manualEntryMode === 'hole') {
      const { front9Total, back9Total, total, allFilled } = calculateManualHoleTotals();
      if (!allFilled) {
        toast.error(`Please enter a score for all ${numberOfHoles} holes`);
        return;
      }

      setSubmitting(true);
      try {
        const holeScoresToSubmit = manualHoleScores.slice(0, numberOfHoles).map(s => parseInt(s));
        const scorePayload = {
          course_id: selectedCourse,
          course_name: course.name,
          date: format(new Date(), 'yyyy-MM-dd'),
          total_score: total,
          ...roundParPayload(course, numberOfHoles, startingNine),
          front_9: front9Total,
          back_9: numberOfHoles === 18 ? back9Total : null,
          hole_scores: holeScoresToSubmit,
          holes_played: numberOfHoles,
          notes: notes.trim() || `${numberOfHoles}-hole round: ${holeScoresToSubmit.join(', ')}`
        };
        
        const response = await axios.post(`${API}/scores`, scorePayload);
        
        // Delete draft after successful save
        try {
          await axios.delete(`${API}${scoreDraftPath(draftId)}`);
        } catch (e) { /* ignore */ }
        
        toast.success("Score saved!");
        
        // Refresh profile to get updated handicap
        if (refreshProfile) {
          const updatedProfile = await refreshProfile();
          if (updatedProfile?.handicap !== undefined) {
            setLatestHandicap(updatedProfile.handicap);
          }
        }
        
        // Prepare data for potential sharing
        // Parse scores to integers for proper display
        const parsedHoleScores = holeScoresToSubmit.map((score, i) => {
          const scoreNum = parseInt(score);
          return { 
            hole: i + 1, 
            score: isNaN(scoreNum) ? null : scoreNum,
            par: course.holes?.[i]?.par || 4
          };
        });
        
        setSavedScoreData({
          ...scorePayload,
          score_id: response.data?.score_id,
          hole_scores: parsedHoleScores,
          course_pars: course.holes || []
        });
        
        // Show quick post-save options panel
        setShowPostSaveOptions(true);
      } catch (error) {
        toast.error(error.message || "Failed to save score");
      } finally {
        setSubmitting(false);
      }
    } else {
      if (!front9 || (numberOfHoles === 18 && !back9)) {
        toast.error("Please enter the total for each selected nine");
        return;
      }

      setSubmitting(true);
      try {
        const scorePayload = {
          course_id: selectedCourse,
          course_name: course.name,
          date: format(new Date(), 'yyyy-MM-dd'),
          total_score: totalScore,
          ...roundParPayload(course, numberOfHoles, startingNine),
          notes: notes.trim() ? `Front 9: ${front9}, Back 9: ${back9}. ${notes.trim()}` : `Front 9: ${front9}, Back 9: ${back9}`
        };
        
        const response = await axios.post(`${API}/scores`, scorePayload);
        toast.success("Score saved!");
        
        // Refresh profile to get updated handicap
        if (refreshProfile) {
          const updatedProfile = await refreshProfile();
          if (updatedProfile?.handicap !== undefined) {
            setLatestHandicap(updatedProfile.handicap);
          }
        }
        
        // Prepare data for potential sharing 
        // If we have scanned hole scores, include them for the shareable scorecard
        const parsedHoleScores = scannedHoleScoresForTotal.length > 0 
          ? scannedHoleScoresForTotal.map((score, i) => {
              const scoreNum = parseInt(score);
              return { 
                hole: i + 1, 
                score: isNaN(scoreNum) ? null : scoreNum,
                par: course.hole_pars?.[i] || course.holes?.[i]?.par || 4
              };
            })
          : null;
        
        setSavedScoreData({
          ...scorePayload,
          score_id: response.data?.score_id,
          front_9: parseInt(front9),
          back_9: parseInt(back9),
          hole_scores: parsedHoleScores,
          course_pars: course.hole_pars || course.holes || []
        });
        
        // Show quick post-save options panel
        setShowPostSaveOptions(true);
      } catch (error) {
        toast.error(error.message || "Failed to save score");
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Handle share scorecard to feed
  const handleShareToFeed = async (imageData, scoreData) => {
    try {
      const course = courses.find(c => c.course_id === selectedCourse);
      const relativeToPar = scoreData.total_score - scoreData.par;
      const relativeString = relativeToPar === 0 ? 'E' : (relativeToPar > 0 ? `+${relativeToPar}` : relativeToPar);
      
      // Create post with scorecard content
      const postContent = `Just finished a round at ${course?.name || scoreData.course_name}!\n\nScore: ${scoreData.total_score} (${relativeString})\n\n#ParSeeker #GolfLife`;
      
      await axios.post(`${API}/posts`, {
        content: postContent,
        post_type: 'round_update',
        image_url: imageData, // The scorecard image as base64
        visibility: 'public'
      });
      
      toast.success("Scorecard shared to feed!");
      setShowShareScorecard(false);
      navigate('/home');
    } catch (error) {
      console.error('Error sharing to feed:', error);
      toast.error("Failed to share to feed");
    }
  };

  // Close share modal and navigate to dashboard
  const handleCloseShareModal = () => {
    setShowShareScorecard(false);
    navigate('/dashboard');
  };

  // Scanner handlers
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setResult(null);
      setEditedHoles([]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.type.startsWith('image/')) {
        toast.error("Please drop an image file");
        return;
      }
      setFile(droppedFile);
      setPreview(URL.createObjectURL(droppedFile));
      setResult(null);
      setEditedHoles([]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
    setResult(null);
    setEditedHoles([]);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setEditedHoles([]);
    setCourseName('');
    setShowVerification(false);
    setVerifiedScores(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const analyzeScorecard = async () => {
    if (!file) {
      toast.error("Please select a scorecard image first");
      return;
    }

    setAnalyzing(true);
    setShowVerification(false);
    setVerifiedScores(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/scorecard/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setResult(response.data);
        setEditedHoles(response.data.holes.map(h => ({ ...h })));
        setCourseName(response.data.course_name || '');
        
        // Also update manualHoleScores so they sync if user switches to manual mode
        const scannedScores = response.data.holes.map(h => h.score !== null ? String(h.score) : '');
        // Pad to 18 holes if needed
        while (scannedScores.length < 18) {
          scannedScores.push('');
        }
        setManualHoleScores(scannedScores);
        
        // Show verification modal with detected scores
        setShowVerification(true);
        
        // Check if there are any low confidence scores
        const lowConfidenceCount = response.data.holes.filter(h => h.score_confidence === 'low').length;
        const mediumConfidenceCount = response.data.holes.filter(h => h.score_confidence === 'medium').length;
        
        if (lowConfidenceCount > 0) {
          toast.warning(`${lowConfidenceCount} score(s) need verification - handwriting was unclear`);
        } else if (mediumConfidenceCount > 0) {
          toast.info("Scorecard analyzed! Please verify the highlighted scores.");
        } else {
          toast.success("Scorecard analyzed with high confidence! Please verify before saving.");
        }
      } else {
        toast.error(response.data.message || "Could not analyze scorecard");
        setResult(response.data);
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze scorecard");
    } finally {
      setAnalyzing(false);
    }
  };

  const updateHoleValue = (index, field, value) => {
    const updated = [...editedHoles];
    if (field === 'par' || field === 'score') {
      const numValue = value === '' ? null : parseInt(value);
      updated[index] = { ...updated[index], [field]: numValue };
    }
    setEditedHoles(updated);
  };

  const calculateTotals = () => {
    // Only calculate based on the number of holes being played
    const holesToUse = editedHoles.slice(0, numberOfHoles);
    const totalPar = holesToUse.reduce((sum, h) => sum + (h.par || 0), 0);
    // Check if all holes have valid scores (including 0 and positive numbers)
    const allHolesHaveScores = holesToUse.every(h => typeof h.score === 'number' && h.score >= 0);
    const totalScoreCalc = allHolesHaveScores && holesToUse.length > 0
      ? holesToUse.reduce((sum, h) => sum + h.score, 0) 
      : null;
    return { totalPar, totalScore: totalScoreCalc };
  };

  const saveRound = async () => {
    // Use selectedCourse if available, otherwise fall back to courseName from scan
    if (!selectedCourse && !courseName.trim()) {
      toast.error("Please select a course");
      return;
    }

    const { totalPar, totalScore: scanTotalScore } = calculateTotals();
    
    if (scanTotalScore === null) {
      toast.error("Please enter a score for every hole");
      return;
    }

    setSaving(true);
    try {
      let courseId;
      let courseNameToUse;
      
      if (selectedCourse) {
        // Use selected course from dropdown
        const course = courses.find(c => c.course_id === selectedCourse);
        courseId = selectedCourse;
        courseNameToUse = course?.name || 'Unknown Course';
      } else {
        // Fall back to course name from scan result
        const coursesRes = await axios.get(`${API}/courses`);
        const existingCourse = coursesRes.data.find(c => c.name.toLowerCase() === courseName.trim().toLowerCase());
        
        if (existingCourse) {
          courseId = existingCourse.course_id;
          courseNameToUse = existingCourse.name;
        } else {
          const newCourseRes = await axios.post(`${API}/courses`, {
            name: courseName.trim(),
            par: totalPar
          });
          courseId = newCourseRes.data.course_id;
          courseNameToUse = courseName.trim();
        }
      }

      // Only save holes up to numberOfHoles
      const holesToSave = editedHoles.slice(0, numberOfHoles);
      
      await axios.post(`${API}/scores`, {
        course_id: courseId,
        course_name: courseNameToUse,
        date: format(new Date(), 'yyyy-MM-dd'),
        total_score: scanTotalScore,
        par: totalPar,
        holes_played: numberOfHoles,
        starting_nine: startingNine,
        course_pars: holesToSave.map(h => h.par),
        hole_scores: holesToSave.map(h => h.score),
        front_9: holesToSave.slice(0, 9).reduce((sum, h) => sum + (h.score || 0), 0),
        back_9: numberOfHoles > 9 ? holesToSave.slice(9, 18).reduce((sum, h) => sum + (h.score || 0), 0) : null,
        notes: `Scanned scorecard (${numberOfHoles} holes) - Hole-by-hole: ${holesToSave.map(h => h.score).join(', ')}`
      });

      await refreshSubscription();
      toast.success("Round saved successfully!");
      navigate('/dashboard');
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save round");
    } finally {
      setSaving(false);
    }
  };

  const clearDraft = async () => {
    if (!window.confirm("Clear this draft? This will reset all scores and start fresh.")) {
      return;
    }
    
    try {
      // Delete draft from server
      await axios.delete(`${API}${scoreDraftPath(draftId)}`);
      
      // Reset all form state
      setManualHoleScores(Array(18).fill(''));
      setFront9('');
      setBack9('');
      setSelectedCourse(null);
      setFile(null);
      setPreview(null);
      setResult(null);
      setEditedHoles([]);
      setCourseName('');
      setHoleEntryMethod('manual');
      setManualEntryMode('hole');
      setVoiceScores(Array(18).fill(null));
      setCurrentVoiceHole(1);
      setStartingNine('front');
      setFirstNineSelection(null);
      setSecondNineSelection(null);
      
      toast.success("Draft cleared! Start fresh.");
    } catch (error) {
      console.error("Clear draft error:", error);
      // Even if server delete fails, reset local state
      setManualHoleScores(Array(18).fill(''));
      setFront9('');
      setBack9('');
      setFile(null);
      setPreview(null);
      setResult(null);
      setEditedHoles([]);
      setVoiceScores(Array(18).fill(null));
      toast.success("Draft cleared!");
    }
  };

  // Stop all currently playing audio
  const stopAllAudio = () => {
    // Stop welcome audio
    if (welcomeAudioRef.current) {
      welcomeAudioRef.current.pause();
      welcomeAudioRef.current.currentTime = 0;
      welcomeAudioRef.current = null;
    }
    // Stop current prompt audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingAudioRef.current = false;
  };

  // Speak any prompt using the selected voice (stops previous audio first)
  const speakPrompt = async (text) => {
    if (!audioFeedbackEnabled) return;
    
    // Stop any currently playing audio first
    stopAllAudio();
    
    try {
      const response = await axios.post(`${API}/voice/speak`, {
        text: text,
        voice: selectedVoice
      });

      if (response.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
        currentAudioRef.current = audio;
        audio.onended = () => {
          currentAudioRef.current = null;
        };
        audio.play().catch(err => {
          console.error("Audio play error:", err);
          currentAudioRef.current = null;
        });
      }
    } catch (error) {
      console.error("Speak prompt error:", error);
    }
  };

  // Handle course selection - triggers play mode selector
  const handleCourseSelected = async (courseId, courseData = null) => {
    // Stop any playing audio immediately
    stopAllAudio();
    
    soundEffects.playGolfSwing();
    setSelectedCourse(courseId);
    setRequireCourseFirst(false); // Course now selected
    
    // Reset tee selection
    setAvailableTees([]);
    setSelectedTee(null);
    setHoleYardages([]);
    setHoleHandicaps([]);
    
    // Store the full course data if provided (from lookup)
    if (courseData) {
      // Check if course has hole_pars - if not, it's a stored course without detailed data
      if (!courseData.hole_pars || courseData.hole_pars.length === 0) {
        // Try to fetch hole_pars from API using course name
        try {
          const response = await axios.post(`${API}/voice/lookup-course`, {
            course_name: courseData.name
          });
          
          if (response.data.success && response.data.course) {
            const apiCourse = response.data.course;
            // Merge API data with existing course data
            const enrichedCourseData = {
              ...courseData,
              par: apiCourse.par || courseData.par,
              hole_pars: apiCourse.hole_pars || [],
              hole_handicaps: apiCourse.hole_handicaps || [],
              tees: apiCourse.tees || [],
              location: apiCourse.location || courseData.location
            };
            setFoundCourse(enrichedCourseData);
            
            // Set available tees for yardage selection
            if (apiCourse.tees && apiCourse.tees.length > 0) {
              setAvailableTees(apiCourse.tees);
              // Auto-select first tee with yardages
              const defaultTee = apiCourse.tees.find(t => t.hole_yardages && t.hole_yardages.length > 0) || apiCourse.tees[0];
              if (defaultTee) {
                setSelectedTee(defaultTee);
                setHoleYardages(defaultTee.hole_yardages || []);
              }
            }
            
            // Set hole handicaps
            if (apiCourse.hole_handicaps && apiCourse.hole_handicaps.length > 0) {
              setHoleHandicaps(apiCourse.hole_handicaps);
            }
            
            // Update the stored course with hole_pars
            if (apiCourse.hole_pars && apiCourse.hole_pars.length > 0) {
              try {
                await axios.put(`${API}/courses/${courseId}`, {
                  hole_pars: apiCourse.hole_pars,
                  par: apiCourse.par
                });
              } catch (updateError) {
                console.log("Could not update stored course with hole pars:", updateError);
              }
            }
          } else {
            setFoundCourse(courseData);
          }
        } catch (error) {
          console.log("Could not fetch course details from API:", error);
          setFoundCourse(courseData);
        }
      } else {
        // Course already has hole_pars - check for tees
        setFoundCourse(courseData);
        if (courseData.tees && courseData.tees.length > 0) {
          setAvailableTees(courseData.tees);
          const defaultTee = courseData.tees.find(t => t.hole_yardages && t.hole_yardages.length > 0) || courseData.tees[0];
          if (defaultTee) {
            setSelectedTee(defaultTee);
            setHoleYardages(defaultTee.hole_yardages || []);
          }
        }
        // Set hole handicaps from cached course data
        if (courseData.hole_handicaps && courseData.hole_handicaps.length > 0) {
          setHoleHandicaps(courseData.hole_handicaps);
        }
      }
    }
    
    // Show play mode selector
    setShowPlayModeSelector(true);
    
    // Scroll to top to show 9/18 hole selector
    scrollToTop();
  };

  // Handle play mode selection
  const handlePlayModeSelect = (mode) => {
    // Stop any playing audio immediately
    stopAllAudio();
    
    setPlayMode(mode);
    setShowPlayModeSelector(false);
    // No voice prompts - user proceeds silently
    // For skins and matchplay, the PlayModeSelector handles navigation
    
    // Scroll to top to show 9/18 hole selector
    scrollToTop();
  };

  // Handle Group Round mode - opens player selection
  const handleGroupRoundMode = () => {
    setShowGroupRoundSetup(true);
  };

  // Handle starting a group round (after player selection)
  const handleStartGroupRound = (roundData) => {
    setGroupRoundData(roundData);
    setShowGroupRoundSetup(false);
    // Navigate to the group round live page with selected scorecard type
    navigate(`/group-round/${roundData.roundId}`, { 
      state: { 
        roundData,
        course: foundCourse || courses.find(c => c.course_id === selectedCourse),
        holes: roundData.holes || numberOfHoles,
        startingNine: roundData.startingNine || startingNine,
        scorecardType: roundData.scorecardType || 'quickcard'
      }
    });
  };

  // Voice Welcome function - disabled (instructional voice removed)
  const playVoiceWelcome = async () => {
    // No longer plays welcome audio
    setVoiceIntroPlayed(true);
  };

  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop()); // Stop immediately, just testing permission
      setMicPermissionGranted(true);
      toast.success("Microphone access granted!");
      
      // Show voice mode choice dialog
      setShowVoiceModeChoice(true);
    } catch (error) {
      console.error("Mic permission denied:", error);
      toast.error("Microphone access denied. Voice entry won't work without it.");
      setMicPermissionGranted(false);
    }
  };

  // Handle voice mode selection
  const handleVoiceModeChoice = async (mode) => {
    setVoiceInputMode(mode);
    setShowVoiceModeChoice(false);
    
    if (mode === 'interactive') {
      // Start continuous wake word listening (no voice prompt)
      startWakeWordListener();
    }
    // No voice prompts for mode selection - user proceeds silently
  };

  // Play setup reminders - disabled (instructional voice removed)
  const playSetupReminders = async () => {
    // No longer plays setup reminders
  };

  // Play Miss Birdie dialog with audio queue to prevent overlap
  const playBirdieDialog = async (dialogType, extraData = {}) => {
    return new Promise(async (resolve) => {
      try {
        const response = await axios.post(`${API}/voice/birdie-dialog`, {
          type: dialogType,
          voice: selectedVoice,
          ...extraData
        });
        
        if (response.data.audio_base64) {
          // Add to queue and process
          audioQueueRef.current.push({
            audioBase64: response.data.audio_base64,
            text: response.data.text,
            resolve
          });
          processAudioQueue();
        } else {
          resolve(response.data.text);
        }
      } catch (e) {
        console.error("Birdie dialog error:", e);
        resolve(null);
      }
    });
  };

  // Process audio queue sequentially to prevent overlap
  const processAudioQueue = async () => {
    if (isPlayingAudioRef.current || audioQueueRef.current.length === 0) {
      return;
    }
    
    isPlayingAudioRef.current = true;
    const item = audioQueueRef.current.shift();
    
    try {
      const audio = new Audio(`data:audio/mp3;base64,${item.audioBase64}`);
      
      audio.onended = () => {
        isPlayingAudioRef.current = false;
        item.resolve(item.text);
        // Process next item in queue
        if (audioQueueRef.current.length > 0) {
          setTimeout(() => processAudioQueue(), 300); // Small gap between audio
        }
      };
      
      audio.onerror = () => {
        isPlayingAudioRef.current = false;
        item.resolve(item.text);
        processAudioQueue();
      };
      
      await audio.play();
    } catch (e) {
      console.error("Audio play error:", e);
      isPlayingAudioRef.current = false;
      item.resolve(item.text);
      processAudioQueue();
    }
  };

  // Course Lookup Functions
  const startCourseLookup = async () => {
    setCourseLookupMode(true);
    setCourseLookupStep('asking_name');
    // No voice prompt - user sees the form
  };

  const handleCourseLookup = async (courseName, location) => {
    setCourseLookupStep('searching');
    setExistingCourseMatch(null);
    // No voice prompt during search
    
    // First, check if we already have this course saved (fuzzy match by name)
    const normalizedSearchName = courseName.toLowerCase().trim();
    const existingMatch = courses.find(c => {
      const normalizedCourseName = c.name.toLowerCase().trim();
      // Check for exact match or if one contains the other
      return normalizedCourseName === normalizedSearchName ||
             normalizedCourseName.includes(normalizedSearchName) ||
             normalizedSearchName.includes(normalizedCourseName);
    });
    
    if (existingMatch) {
      // Found an existing course - prompt user
      setExistingCourseMatch(existingMatch);
      setCourseLookupStep('existing_found');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/voice/lookup-course`, {
        course_name: courseName
        // Note: location is NOT sent - search by name only, user selects from results
      });
      
      if (response.data.success) {
        // Handle multiple results - show list to select from
        if (response.data.multiple_results && response.data.courses) {
          setSearchResults(response.data.courses);
          setCourseLookupStep('select_from_list');
        } else if (response.data.course) {
          const course = response.data.course;
          setFoundCourse(course);
          
          // Check if it's a multi-course facility with multiple nines/courses to choose from
          if (course.nines && course.nines.length >= 2) {
            setCourseLookupStep('select_nines');
          } else {
            // Show swipeable confirmation card
            setShowCourseConfirmCard(true);
            setCourseLookupStep('swipe_confirm');
          }
        } else {
          setCourseLookupStep('not_found');
        }
      } else {
        setCourseLookupStep('not_found');
      }
    } catch (error) {
      console.error("Course lookup error:", error);
      setCourseLookupStep('not_found');
    }
  };

  // Use existing course from saved courses
  const useExistingCourse = () => {
    if (existingCourseMatch) {
      // Validate that saved course has complete hole_pars data
      const holePars = existingCourseMatch.hole_pars || [];
      const hasValidPars = holePars.length >= 9;
      
      if (!hasValidPars) {
        // Course data is incomplete - force reload from API
        toast.info(`Reloading complete course data for ${existingCourseMatch.name}...`);
        reloadCourse();
        return;
      }
      
      handleCourseSelected(existingCourseMatch.course_id, existingCourseMatch);
      setCourseMenuExpanded(false);
      setExistingCourseMatch(null);
      setCourseLookupStep('idle');
      toast.success(`Loaded ${existingCourseMatch.name}`);
      
      // Scroll to top to show 9/18 hole selector
      scrollToTop();
    }
  };

  // Reload/refresh course data from API
  const reloadCourse = async () => {
    setExistingCourseMatch(null);
    setCourseLookupStep('searching');
    
    try {
      const response = await axios.post(`${API}/voice/lookup-course`, {
        course_name: pendingCourseName
        // Note: location is NOT sent - search by name only
      });
      
      if (response.data.success) {
        // Handle multiple results - show list to select from
        if (response.data.multiple_results && response.data.courses) {
          setSearchResults(response.data.courses);
          setCourseLookupStep('select_from_list');
        } else if (response.data.course) {
          const course = response.data.course;
          setFoundCourse(course);
          
          if (course.nines && course.nines.length >= 2) {
            setCourseLookupStep('select_nines');
          } else {
            setCourseLookupStep('confirm');
          }
        } else {
          setCourseLookupStep('not_found');
        }
      } else {
        setCourseLookupStep('not_found');
      }
    } catch (error) {
      console.error("Course reload error:", error);
      setCourseLookupStep('not_found');
    }
  };

  // Handle selection from search results list
  const [searchResults, setSearchResults] = useState([]);
  
  const selectCourseFromList = async (courseId) => {
    setCourseLookupStep('searching');
    try {
      const response = await axios.get(`${API}/courses/golfapi/${courseId}`);
      if (response.data.success && response.data.course) {
        const course = response.data.course;
        setFoundCourse(course);
        setSearchResults([]);
        
        if (course.nines && course.nines.length >= 2) {
          setCourseLookupStep('select_nines');
        } else {
          setCourseLookupStep('confirm');
        }
      } else {
        toast.error('Failed to fetch course details');
        setCourseLookupStep('not_found');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to fetch course details');
      setCourseLookupStep('not_found');
    }
  };

  // Handle nine selection for 27-hole courses
  const handleNineSelection = async (frontNineIndex, backNineIndex) => {
    if (!foundCourse || !foundCourse.nines) return;
    
    const frontNine = foundCourse.nines[frontNineIndex];
    const backNine = foundCourse.nines[backNineIndex];
    
    // Combine the two nines into hole_pars
    const combinedHolePars = [...frontNine.hole_pars, ...backNine.hole_pars];
    const combinedPar = frontNine.par + backNine.par;
    
    // Update foundCourse with selected configuration
    setFoundCourse({
      ...foundCourse,
      hole_pars: combinedHolePars,
      par: combinedPar,
      selectedNines: {
        front: frontNine.name,
        back: backNine.name
      }
    });
    
    setSelectedNines({ front: frontNineIndex, back: backNineIndex });
    setCourseLookupStep('confirm');
    
    const confirmText = `Great! Playing ${frontNine.name} and ${backNine.name} today. Par ${combinedPar}. Sound good?`;
    await speakText(confirmText);
  };

  const confirmFoundCourse = async () => {
    if (!foundCourse) return;
    
    try {
      const response = await axios.post(`${API}/courses/create-from-lookup`, {
        name: foundCourse.name,
        par: foundCourse.par,
        location: foundCourse.location,
        hole_pars: foundCourse.hole_pars,
        hole_handicaps: foundCourse.hole_handicaps || [],
        tees: foundCourse.tees || [],
        selected_nines: foundCourse.selectedNines || null
      });
      
      // Refresh courses list
      const coursesRes = await axios.get(`${API}/courses`);
      setCourses(coursesRes.data);
      
      // Store the FULL course data for play mode selector
      const savedCourseData = {
        course_id: response.data.course_id,
        name: foundCourse.name,
        par: foundCourse.par,
        hole_pars: foundCourse.hole_pars || [],
        hole_handicaps: foundCourse.hole_handicaps || [],
        tees: foundCourse.tees || []
      };
      
      // Set yardages and handicaps directly from the found course data
      if (foundCourse.tees && foundCourse.tees.length > 0) {
        setAvailableTees(foundCourse.tees);
        const defaultTee = foundCourse.tees.find(t => t.hole_yardages && t.hole_yardages.length > 0) || foundCourse.tees[0];
        if (defaultTee) {
          setSelectedTee(defaultTee);
          setHoleYardages(defaultTee.hole_yardages || []);
        }
      }
      
      if (foundCourse.hole_handicaps && foundCourse.hole_handicaps.length > 0) {
        setHoleHandicaps(foundCourse.hole_handicaps);
      }
      
      // Reset lookup state
      setCourseLookupMode(false);
      setCourseLookupStep('idle');
      setPendingCourseName('');
      setPendingCourseLocation('');
      
      // Trigger play mode selection with voice prompt
      handleCourseSelected(response.data.course_id, savedCourseData);
      
    } catch (error) {
      console.error("Create course error:", error);
      toast.error("Failed to create course");
    }
  };

  // Handle swipe confirm on course card
  const handleSwipeConfirmCourse = async () => {
    setShowCourseConfirmCard(false);
    toast.loading("Setting up your course...", { id: "course-confirm" });
    try {
      await confirmFoundCourse();
      toast.success("Course ready! Select your game.", { id: "course-confirm" });
    } catch (error) {
      toast.error("Failed to set up course. Try again.", { id: "course-confirm" });
    }
  };

  // Handle swipe reject on course card  
  const handleSwipeRejectCourse = () => {
    setShowCourseConfirmCard(false);
    setFoundCourse(null);
    setCourseLookupStep('idle');
    toast.info("No problem! Search for another course.");
  };

  const speakText = async (text) => {
    // Use audio queue to prevent overlap
    return new Promise(async (resolve) => {
      try {
        const response = await axios.post(`${API}/voice/speak`, {
          text,
          voice: selectedVoice
        });
        if (response.data.audio_base64) {
          // Add to queue and process
          audioQueueRef.current.push({
            audioBase64: response.data.audio_base64,
            text: text,
            resolve
          });
          processAudioQueue();
        } else {
          resolve();
        }
      } catch (e) {
        console.error("Speak error:", e);
        resolve();
      }
    });
  };

  // Wake Word Listener using Web Speech API
  const startWakeWordListener = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Your browser doesn't support voice recognition. Try Chrome or Edge.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      
      toast.success("Listening for 'Par Seeker'...");
    };
    
    recognition.onresult = async (event) => {
      const last = event.results.length - 1;
      const transcript = event.results[last][0].transcript.toLowerCase().trim();
      
      
      setVoiceTranscript(transcript); // Show what was heard
      
      // Check for wake word "par seeker" - more variations
      const wakeWordDetectedInTranscript = 
        transcript.includes('par seeker') || 
        transcript.includes('parkseeker') || 
        transcript.includes('par speaker') ||
        transcript.includes('parseeker') ||
        transcript.includes('par seaker') ||
        transcript.includes('par seek');
        
      if (wakeWordDetectedInTranscript) {
        toast.info("Wake word detected!");
        
        // Check for specific commands
        if (transcript.includes('score')) {
          // "Par Seeker Score X" - extract the number
          const numbers = transcript.match(/\d+/g);
          if (numbers && numbers.length > 0) {
            const score = parseInt(numbers[numbers.length - 1]);
            if (score >= 1 && score <= 15) {
              recognition.stop();
              isListeningRef.current = false;
              setIsListeningForWakeWord(false);
              await handleVoiceScore(currentVoiceHole, score);
              // Resume listening after a short pause
              setTimeout(() => {
                if (voiceInputMode === 'interactive') {
                  startWakeWordListener();
                }
              }, 2000);
            }
          }
        } else if (transcript.includes('save')) {
          recognition.stop();
          isListeningRef.current = false;
          setIsListeningForWakeWord(false);
          await saveVoiceScores();
        } else if (transcript.includes('fast') || transcript.includes('quick') || transcript.includes('hard')) {
          recognition.stop();
          setVoiceMode('quick');
          // No voice prompt - mode changed silently
          setTimeout(() => {
            if (voiceInputMode === 'interactive') {
              startWakeWordListener();
            }
          }, 1000);
        } else if (transcript.includes('slow') || transcript.includes('hole by hole')) {
          recognition.stop();
          setVoiceMode('hole_by_hole');
          // No voice prompt - mode changed silently
          setTimeout(() => {
            if (voiceInputMode === 'interactive') {
              startWakeWordListener();
            }
          }, 1000);
        } else {
          // Just wake word detected - ready for commands
          if (!wakeWordDetected) {
            setWakeWordDetected(true);
            recognition.stop();
            // No voice prompts - just reset and continue listening
            setTimeout(async () => {
              setWakeWordDetected(false);
              if (voiceInputMode === 'interactive') {
                startWakeWordListener();
              }
            }, 1000);
          }
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      if (event.error === 'not-allowed') {
        toast.error("Microphone access denied. Please allow microphone in browser settings.");
        isListeningRef.current = false;
        setIsListeningForWakeWord(false);
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        // For other errors, try to restart
        
      }
    };
    
    recognition.onend = () => {
      
      // Restart if we're still supposed to be listening (use ref for current value)
      if (isListeningRef.current && voiceInputMode === 'interactive') {
        try {
          setTimeout(() => {
            if (isListeningRef.current) {
              recognition.start();
              
            }
          }, 100);
        } catch (e) {
          
        }
      }
    };
    
    try {
      recognition.start();
      isListeningRef.current = true;
      setIsListeningForWakeWord(true);
      speechRecognitionRef.current = recognition;
    } catch (e) {
      console.error("Could not start recognition:", e);
    }
  };

  const stopWakeWordListener = () => {
    isListeningRef.current = false;
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.stop();
      setIsListeningForWakeWord(false);
    }
  };

  // Handle voice score entry
  const handleVoiceScore = async (hole, score) => {
    const newScores = [...voiceScores];
    const newManualScores = [...manualHoleScores];
    
    newScores[hole - 1] = score;
    newManualScores[hole - 1] = String(score);
    
    setVoiceScores(newScores);
    setManualHoleScores(newManualScores);
    
    // Save draft immediately to prevent data loss
    saveDraft(newManualScores);
    
    // Play score entry sound effect
    const holePar = selectedCourseData?.hole_pars?.[hole - 1] || 4;
    if (score < holePar) {
      soundEffects.playBirdie(); // Birdie or better!
    } else {
      soundEffects.playScoreEntry();
    }
    
    // Play confirmation with encouragement
    if (audioFeedbackEnabled) {
      const nextHole = hole < numberOfHoles ? hole + 1 : null;
      try {
        const response = await axios.post(`${API}/voice/confirm-score`, {
          hole,
          score,
          par: holePar,
          next_hole: nextHole,
          voice: selectedVoice
        });
        
        if (response.data.audio_base64) {
          const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
          audio.play();
        }
      } catch (e) {
        console.error("Confirmation error:", e);
      }
    }
    
    // Move to next hole
    if (hole < numberOfHoles) {
      setCurrentVoiceHole(hole + 1);
    } else {
      // Round complete - no voice prompt, just UI notification
      const total = newScores.slice(0, numberOfHoles).reduce((sum, s) => sum + (s || 0), 0);
      toast.success(`Round complete! Total: ${total}`);
    }
    
    toast.success(`Hole ${hole}: ${score}`);
  };

  // Cleanup wake word listener when leaving voice mode
  useEffect(() => {
    return () => {
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
    };
  }, []);

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      // Use audio constraints optimized for iOS
      // Setting echoCancellation to false can help keep audio on speaker
      const audioConstraints = {
        audio: {
          echoCancellation: false,  // Helps keep speaker active on iOS
          noiseSuppression: false,  // Better quality on iOS
          autoGainControl: true,
          channelCount: 1
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
      
      // Check supported mimeTypes - iOS Safari prefers audio/mp4
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';  // iOS Safari prefers this
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }
      
      
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size > 0) {
          await processVoiceInput(audioBlob);
        } else {
          toast.error("No audio recorded. Please try again.");
          setVoiceProcessing(false);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("Score MediaRecorder error:", event.error);
        toast.error("Recording error. Please try again.");
      };

      // Start recording with timeslice for better iOS compatibility
      mediaRecorder.start(500);
      setIsRecording(true);
      toast.info(`Recording for hole ${currentVoiceHole}...`);
    } catch (error) {
      console.error("Microphone access error:", error);
      toast.error("Could not access microphone. Please check permissions in Settings.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVoiceInput = async (audioBlob) => {
    setVoiceProcessing(true);
    setVoiceTranscript('Processing...');

    try {
      // Determine file extension based on blob type
      let fileExt = 'webm';
      if (audioBlob.type.includes('mp4')) fileExt = 'mp4';
      else if (audioBlob.type.includes('ogg')) fileExt = 'ogg';
      
      // Get current hole par for golf term conversion (birdie, bogey, etc.)
      const currentHolePar = selectedCourseData?.hole_pars?.[currentVoiceHole - 1] || 4;
      const actualHoleNumber = getActualHoleNumber(currentVoiceHole - 1);
      
      const formData = new FormData();
      formData.append('file', audioBlob, `voice.${fileExt}`);
      formData.append('current_hole', String(currentVoiceHole));
      formData.append('actual_hole_number', String(actualHoleNumber));
      formData.append('current_hole_par', String(currentHolePar));
      
      // Send all hole pars for multi-hole entries
      if (selectedCourseData?.hole_pars) {
        formData.append('hole_pars', JSON.stringify(selectedCourseData.hole_pars));
      }

      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setVoiceTranscript(response.data.transcript);
        
        // Apply parsed scores
        if (response.data.parsed_scores && response.data.parsed_scores.length > 0) {
          const newScores = [...voiceScores];
          const newManualScores = [...manualHoleScores];
          const scoresEntered = [];
          
          for (const { hole, score } of response.data.parsed_scores) {
            // Use currentVoiceHole if hole is null
            const targetHole = hole !== null ? hole : currentVoiceHole;
            
            if (targetHole >= 1 && targetHole <= 18 && score >= 1 && score <= 15) {
              newScores[targetHole - 1] = score;
              newManualScores[targetHole - 1] = String(score);
              scoresEntered.push({ hole: targetHole, score });
              
              // Move to next hole - respect the nine we're playing
              let nextHole = targetHole + 1;
              if (startingNine === 'back' && numberOfHoles === 9) {
                // Back 9 only: holes 10-18, stop at 18
                if (nextHole > 18) nextHole = 18;
              } else if (startingNine === 'front' && numberOfHoles === 9) {
                // Front 9 only: holes 1-9, stop at 9
                if (nextHole > 9) nextHole = 9;
              } else {
                // 18 holes: stop at 18
                if (nextHole > 18) nextHole = 18;
              }
              setCurrentVoiceHole(nextHole);
            }
          }
          
          setVoiceScores(newScores);
          setManualHoleScores(newManualScores);
          
          // Save draft immediately to prevent data loss
          saveDraft(newManualScores);
          
          // Play ONE confirmation for all scores entered (no overlap)
          if (audioFeedbackEnabled && scoresEntered.length > 0) {
            if (scoresEntered.length === 1) {
              // Single hole - play detailed confirmation
              playScoreConfirmation(scoresEntered[0].hole, scoresEntered[0].score);
            }
            // Multiple holes - just show toast (no voice)
          }
          
          const displayScores = response.data.parsed_scores.map(s => {
            const displayHole = s.hole !== null ? s.hole : currentVoiceHole;
            return `Hole ${displayHole}: ${s.score}`;
          }).join(', ');
          
          toast.success(`Recorded: ${displayScores}`);
        } else if (response.data.message) {
          toast.warning(response.data.message);
        } else {
          toast.warning("Could not understand. Try saying: 'Hole 3, I got a 5'");
        }
      } else {
        toast.error(response.data.message || "Could not process voice input");
      }
    } catch (error) {
      console.error("Voice processing error:", error);
      toast.error("Failed to process voice input");
    } finally {
      setVoiceProcessing(false);
    }
  };

  const playScoreConfirmation = async (hole, score) => {
    // Use audio queue to prevent overlap
    return new Promise(async (resolve) => {
      try {
        // Get actual par for this hole from course data
        let holePar = 4; // Default
        if (selectedCourseData?.hole_pars && selectedCourseData.hole_pars.length >= hole) {
          holePar = selectedCourseData.hole_pars[hole - 1] || 4;
        }
        
        // Get actual course hole number (for back-9-first scenarios)
        const actualHoleNumber = getActualHoleNumber(hole - 1);
        
        const response = await axios.post(`${API}/voice/confirm-score`, {
          hole: actualHoleNumber,  // Use actual course hole number
          score,
          par: holePar,  // Use actual hole par
          voice: selectedVoice  // Use selected voice (nova or onyx)
        });

        if (response.data.audio_base64) {
          // Add to queue and process
          audioQueueRef.current.push({
            audioBase64: response.data.audio_base64,
            text: `Hole ${actualHoleNumber}: ${score}`,
            resolve
          });
          processAudioQueue();
        } else {
          resolve();
        }
      } catch (error) {
        console.error("Audio feedback error:", error);
        resolve();
      }
    });
  };

  const saveVoiceScores = async () => {
    if (!selectedCourse) {
      toast.error("Please select a course first!");
      // No voice prompt - just toast message
      return;
    }

    const scoresToSave = voiceScores.slice(0, numberOfHoles);
    const allFilled = scoresToSave.every(s => s !== null);
    
    if (!allFilled) {
      toast.error(`Please enter scores for all ${numberOfHoles} holes`);
      return;
    }

    setSaving(true);
    try {
      const course = courses.find(c => c.course_id === selectedCourse);
      const total = scoresToSave.reduce((sum, s) => sum + s, 0);
      const front9Total = scoresToSave.slice(0, 9).reduce((sum, s) => sum + (s || 0), 0);
      const back9Total = numberOfHoles > 9 ? scoresToSave.slice(9).reduce((sum, s) => sum + (s || 0), 0) : null;

      await axios.post(`${API}/scores`, {
        course_id: selectedCourse,
        course_name: course.name,
        date: format(new Date(), 'yyyy-MM-dd'),
        total_score: total,
        ...roundParPayload(course, numberOfHoles, startingNine),
        front_9: front9Total,
        back_9: back9Total,
        hole_scores: scoresToSave,
        holes_played: numberOfHoles,
        notes: `Voice entry - ${numberOfHoles} holes`
      });

      // Delete draft
      try {
        await axios.delete(`${API}${scoreDraftPath(draftId)}`);
      } catch (e) { /* ignore */ }

      await refreshSubscription();
      toast.success("Score saved!");
      navigate('/dashboard');
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save score");
    } finally {
      setSaving(false);
    }
  };

  const selectedCourseData = courses.find(c => c.course_id === selectedCourse);

  const { totalPar, totalScore: scanTotalScore } = calculateTotals();
  const scoreDiff = scanTotalScore !== null ? scanTotalScore - totalPar : null;

  if (resumingDraft && resumeDraft.status !== 'ready') return <div className="ps-theme-page max-w-xl mx-auto p-6 space-y-4" data-testid="resume-round-state">
    {resumeDraft.status === 'loading' ? <p role="status" data-testid="resume-round-loading">Opening your saved round…</p> : <>
      <p role="alert" data-testid="resume-round-error">{resumeDraft.error}</p>
      <Button type="button" data-testid="resume-round-retry" onClick={resumeDraft.retry}>Try again</Button>
    </>}
    <Button type="button" variant="outline" data-testid="resume-round-back" onClick={() => navigate('/tournaments')}>Back to games</Button>
  </div>;

  // Show setup carousel first only for a new round.
  if (showSetupCarousel) {
    return (
      <ScoreSetupCarousel
        registeredCourses={courses}
        onComplete={handleSetupComplete}
        onBack={() => navigate(-1)}
        onExit={() => navigate('/dashboard', { state: { carouselDismissed: true } })}
        user={user}
        initialCourse={foundCourse}
      />
    );
  }

  // After setup complete: Show clean scorecard-only view
  if (setupComplete) {
    // Get current hole being played (first empty hole)
    const getCurrentHole = () => {
      for (let i = 0; i < numberOfHoles; i++) {
        if (!manualHoleScores[i] || manualHoleScores[i] === '') {
          return getActualHoleNumber(i);
        }
      }
      return getActualHoleNumber(numberOfHoles - 1);
    };
    
    const currentHole = getCurrentHole();
    
    return (
      <div className="dark-page max-w-xl mx-auto animate-fade-in" data-testid="add-score-page">
        {/* Manual Hole Info Card */}
        {showManualHoleInfo && foundCourse && (
          <SharedHoleInfoCard
            courseId={selectedCourse || foundCourse?.course_id}
            holeNumber={manualHoleInfoNumber}
            par={(foundCourse.hole_pars || [])[
              startingNine === 'back' ? manualHoleInfoNumber - 10 : manualHoleInfoNumber - 1
            ]}
            yardage={(holeYardages || [])[
              startingNine === 'back' ? manualHoleInfoNumber - 10 : manualHoleInfoNumber - 1
            ]}
            handicap={(holeHandicaps || [])[
              startingNine === 'back' ? manualHoleInfoNumber - 10 : manualHoleInfoNumber - 1
            ]}
            courseName={foundCourse.club_name || foundCourse.name}
            teeName={selectedTee?.name}
            isVisible={true}
            onDismiss={() => setShowManualHoleInfo(false)}
          />
        )}
        
        {/* Clean Scorecard View */}
        <div className="p-4">
          {/* Minimal Header with Hole Info Button */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/tournaments')}
              aria-label="Back to games" data-testid="scorecard-back-to-games"
              className="p-2 -ml-2 text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            {/* Hole Info Quick Access Button */}
            {foundCourse && (
              <button
                onClick={() => {
                  setManualHoleInfoNumber(currentHole);
                  setShowManualHoleInfo(true);
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-900/40 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-900/60 transition-all"
                data-testid="hole-info-btn"
              >
                <Flag className="w-4 h-4" />
                <span className="text-sm font-medium">Hole {currentHole}</span>
              </button>
            )}
            
            {!foundCourse && (
              <div className="text-center">
                <p className="text-sm font-medium text-[var(--ps-text-primary)]">
                  {numberOfHoles} Holes • {startingNine === 'front' ? 'Front 9' : 'Back 9'}
                </p>
              </div>
            )}
            
            <div className="w-10" /> {/* Spacer for centering */}
          </div>

          {resumingDraft && resumeDraft.legacy && <div className="mb-3 space-y-2">
            <p role="status" data-testid="resume-round-legacy-notice" className="text-sm text-[var(--ps-text-secondary)]">This older draft did not save its round length, starting nine or tee. Check the round details before saving.</p>
            <div className="flex flex-wrap gap-3 text-sm">
              <label htmlFor="resume-round-holes">Holes <select id="resume-round-holes" data-testid="resume-round-holes" className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded p-1" value={numberOfHoles} onChange={e => setNumberOfHoles(Number(e.target.value))}><option value={9}>9</option><option value={18}>18</option></select></label>
              <label htmlFor="resume-round-nine">Start <select id="resume-round-nine" data-testid="resume-round-nine" className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded p-1" value={startingNine} onChange={e => setStartingNine(e.target.value)}><option value="front">Front nine</option><option value="back">Back nine</option></select></label>
            </div>
          </div>}
          {resumingDraft && resumeDraft.courseUnavailable && <p role="status" data-testid="resume-course-warning" className="text-sm text-[var(--ps-text-secondary)] mb-3">Course details are unavailable. Your saved scores are preserved.</p>}
          <p className="text-sm text-[var(--ps-text-secondary)] mb-3 break-words" data-testid="scorecard-round-details">{foundCourse?.club_name || foundCourse?.name || 'Saved round'} · {numberOfHoles} holes · {startingNine === 'back' ? 'Back nine first' : 'Front nine first'}{selectedTee?.name ? ` · ${selectedTee.name}` : ''}</p>
          {/* Scorecard Entry */}
          <div className="dark-card rounded-2xl overflow-hidden">
            <ScoreEntryForm
              scoreReactions={holeEntryMethod === 'manual' ? { enabled: scoreAnimationPreference.enabled, context: `${user?.user_id}|${date.toISOString()}` } : undefined}
              courseId={selectedCourse || foundCourse?.course_id}
              eventHoles={numberOfHoles}
              initialScores={manualHoleScores}
              onScoresChange={setManualHoleScores}
              showHolesSelector={false}
              showVoiceOption={holeEntryMethod === 'voice'}
              showScanOption={holeEntryMethod === 'scan'}
              autoSave={true}
              autoSaveEndpoint={`${API}/scores/draft`}
              autoSavePayload={draftPayload}
              submitButtonText="Save Round"
              showTotalDisplay={true}
              holePars={foundCourse?.hole_pars || courses.find(course => course.course_id === selectedCourse)?.hole_pars || []}
              holeYardages={holeYardages}
              holeHandicaps={holeHandicaps}
              showYardages={holeYardages.length > 0}
              darkMode={document.documentElement.classList.contains('theme-dark')}
              startingNine={startingNine}
              getActualHoleNumber={getActualHoleNumber}
              onSave={async ({ scores }) => {
                // Save the round
                try {
                  setSubmitting(true);
                  const holeScoresData = scores.slice(0, numberOfHoles).map((s, idx) => ({
                    hole: getActualHoleNumber(idx),
                    score: parseInt(s) || null
                  }));
                  
                  // Calculate total score
                  const validScores = holeScoresData.filter(h => h.score !== null);
                  const totalScore = validScores.reduce((sum, h) => sum + h.score, 0);
                  
                  // Calculate front 9 and back 9 totals
                  let front9 = null;
                  let back9 = null;
                  
                  if (numberOfHoles === 18) {
                    const front9Scores = holeScoresData.slice(0, 9).filter(h => h.score !== null);
                    const back9Scores = holeScoresData.slice(9, 18).filter(h => h.score !== null);
                    front9 = front9Scores.length === 9 ? front9Scores.reduce((sum, h) => sum + h.score, 0) : null;
                    back9 = back9Scores.length === 9 ? back9Scores.reduce((sum, h) => sum + h.score, 0) : null;
                  } else if (startingNine === 'front') {
                    front9 = validScores.length === 9 ? totalScore : null;
                  } else {
                    back9 = validScores.length === 9 ? totalScore : null;
                  }
                  
                  await axios.post(`${API}/scores`, {
                    course_id: selectedCourse || 'unknown',
                    course_name: foundCourse?.club_name || foundCourse?.name || 'Unknown Course',
                    date: date.toISOString(),
                    total_score: totalScore,
                    front_9: front9,
                    back_9: back9,
                    hole_scores: holeScoresData.map(h => h.score),
                    ...roundParPayload(foundCourse || courses.find(c => c.course_id === selectedCourse), numberOfHoles, startingNine),
                    holes_played: numberOfHoles,
                    starting_nine: startingNine
                  });
                  
                  // Delete draft after successful save
                  try {
                    await axios.delete(`${API}${scoreDraftPath(draftId)}`);
                  } catch (e) {}
                  
                  toast.success('Round saved!');
                  if (refreshProfile) refreshProfile();
                  navigate('/history');
                } catch (error) {
                  toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message || 'Failed to save round');
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              showHoleInfoCards={true}
              courseName={foundCourse?.club_name || foundCourse?.name}
            />
          </div>
        </div>
      </div>
    );
  }

  // Original flow (fallback - shouldn't normally reach here)
  return (
    <div className="dark-page max-w-xl mx-auto animate-fade-in p-4" data-testid="add-score-page">
      
      {/* Swipeable Course Confirmation Card */}
      <CourseConfirmCard
        course={foundCourse}
        onConfirm={handleSwipeConfirmCourse}
        onReject={handleSwipeRejectCourse}
        isVisible={showCourseConfirmCard}
      />

      {/* Single Card for Score Entry */}
      <div className="dark-card" data-testid="play-container">
        <div className="p-4 space-y-4">
          {/* 9/18 Holes Toggle - ref for scrolling */}
          <div ref={topSectionRef} style={{ scrollMarginTop: '120px' }}>
            <HolesSelector
              numberOfHoles={numberOfHoles}
              onHolesChange={setNumberOfHoles}
              startingNine={startingNine}
              onStartingNineChange={setStartingNine}
            />
          </div>

          {/* Find Your Course - Collapsible Banner */}
          {!selectedCourse && (
            <div 
              className="dark-card border-2 border-[var(--ps-primary)]/40 overflow-hidden"
              style={{ backgroundColor: 'var(--ps-bg-surface)' }}
            >
              {/* Clickable Banner Header */}
              <button
                onClick={() => setCourseMenuExpanded(!courseMenuExpanded)}
                className="w-full p-4 flex items-center justify-between hover:bg-[var(--ps-bg-hover)] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <img 
                    src="/images/par-seeker-logo.png" 
                    alt="Par Seeker" 
                    className="w-12 h-12 object-contain"
                  />
                  <div className="text-left">
                    <h4 className="font-bold text-[var(--ps-primary)]">FIND YOUR COURSE</h4>
                    <p className="text-xs text-[var(--ps-text-secondary)]">
                      Search or add manually
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-[var(--ps-primary)] transition-transform ${courseMenuExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* Expandable Course Menu */}
              {courseMenuExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-[var(--ps-border)]">
                  {/* Course Lookup Inputs - Always show when not in confirm/select_nines state */}
                  {courseLookupStep !== 'confirm' && courseLookupStep !== 'select_nines' && (
                    <>
                      {/* Searching State */}
                      {courseLookupStep === 'searching' && (
                        <div className="text-center py-4">
                          <div className="dark-spinner w-10 h-10 mx-auto" />
                          <p className="text-[var(--ps-primary)] font-medium mt-3">Looking up your course...</p>
                        </div>
                      )}

                      {/* Multiple Courses Found - Select from list */}
                      {courseLookupStep === 'select_from_list' && searchResults.length > 0 && (
                        <div className="bg-[var(--ps-info)]/10 border-2 border-[var(--ps-info)]/30 rounded-xl p-4 space-y-3">
                          <div className="text-center">
                            <Search className="w-8 h-8 text-[var(--ps-info)] mx-auto" />
                            <h5 className="font-bold text-[var(--ps-info)] mt-1">Select Your Course</h5>
                            <p className="text-sm text-[var(--ps-text-secondary)]">Found {searchResults.length} matching courses</p>
                          </div>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {searchResults.map((course) => (
                              <button
                                key={course.id}
                                onClick={() => selectCourseFromList(course.id)}
                                className="w-full text-left p-3 bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg hover:bg-[var(--ps-bg-hover)] hover:border-[var(--ps-info)] transition-colors"
                              >
                                <div className="font-medium text-[var(--ps-text-primary)]">{course.club_name}</div>
                                {course.course_name && course.course_name !== course.club_name && (
                                  <div className="text-sm text-[var(--ps-info)]">{course.course_name}</div>
                                )}
                                {course.location && (
                                  <div className="text-xs text-[var(--ps-text-muted)] mt-1">{course.location}</div>
                                )}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => {
                              setSearchResults([]);
                              setCourseLookupStep('idle');
                            }}
                            className="w-full py-2 text-xs text-[var(--ps-text-muted)] hover:text-[var(--ps-text-secondary)]"
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      {/* Existing Course Found - Prompt to use or reload */}
                      {courseLookupStep === 'existing_found' && existingCourseMatch && (
                        <div className="bg-[var(--ps-info)]/10 border-2 border-[var(--ps-info)]/30 rounded-xl p-4 space-y-3">
                          <div className="text-center">
                            <FileText className="w-8 h-8 text-[var(--ps-info)] mx-auto" />
                            <h5 className="font-bold text-[var(--ps-info)] mt-1">Course Already Saved!</h5>
                            <p className="text-sm text-[var(--ps-text-primary)] mt-1">{existingCourseMatch.name}</p>
                            <p className="text-sm text-[var(--ps-text-secondary)]">Par {existingCourseMatch.par}</p>
                            {/* Show hole pars status */}
                            {existingCourseMatch.hole_pars?.length >= 9 ? (
                              <p className="text-xs text-green-600 mt-1">✓ {existingCourseMatch.hole_pars.length} hole pars saved</p>
                            ) : (
                              <p className="text-xs text-amber-600 mt-1">⚠ Hole pars need to be loaded</p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={useExistingCourse}
                              className="w-full py-3 text-sm font-bold text-white bg-[var(--ps-info)] rounded-lg hover:opacity-90 flex items-center justify-center gap-2"
                            >
                              <Zap className="w-4 h-4" /> Use This Course
                            </button>
                            <button
                              onClick={reloadCourse}
                              className="w-full py-2 text-sm font-medium text-[var(--ps-info)] bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg hover:bg-[var(--ps-bg-hover)]"
                            >
                              <RefreshCw className="w-4 h-4 inline mr-1" /> Reload Course Data
                            </button>
                            <button
                              onClick={() => {
                                setExistingCourseMatch(null);
                                setCourseLookupStep('idle');
                              }}
                              className="w-full py-2 text-xs text-[var(--ps-text-muted)] hover:text-[var(--ps-text-secondary)]"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Default: Search Input - show when idle or no course lookup in progress */}
                      {(courseLookupStep === 'idle' || !courseLookupStep || courseLookupStep === 'not_found') && (
                        <>
                          {/* Show error message if not found */}
                          {courseLookupStep === 'not_found' && (
                            <div className="bg-[var(--ps-warning)]/10 border border-[var(--ps-warning)]/30 rounded-lg p-3 mb-3">
                              <p className="text-sm text-[var(--ps-warning)] text-center">
                                Couldn't find that course. Try a different spelling or add manually.
                              </p>
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-[var(--ps-text-secondary)]">Course Name</label>
                            <input
                              type="text"
                              value={pendingCourseName}
                              onChange={(e) => setPendingCourseName(e.target.value)}
                              placeholder="e.g., Cherry Creek, Pebble Beach"
                              className="w-full h-12 px-4 rounded-xl text-base bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] border border-[var(--ps-border)] focus:border-[var(--ps-primary)] outline-none"
                              data-testid="course-search-input"
                            />
                            <p className="text-xs text-[var(--ps-text-muted)]">Enter course name, then select from matching results</p>
                          </div>

                          <button
                            onClick={() => { 
                              soundEffects.playClick(); 
                              setFoundCourse(null); // Reset any previous found course
                              handleCourseLookup(pendingCourseName, ''); 
                            }}
                            disabled={!pendingCourseName || courseLookupStep === 'searching'}
                            className="dark-btn-primary w-full py-3 font-bold flex items-center justify-center gap-2"
                          >
                            <Search className="w-5 h-5" /> Find My Course
                          </button>
                          
                          <button
                            onClick={() => {
                              setShowNewCourse(true);
                              setCourseMenuExpanded(false);
                            }}
                            className="w-full text-center text-sm text-[var(--ps-primary)] hover:text-[var(--ps-primary-light)] font-medium"
                          >
                            Can't find your course? Add manually →
                          </button>
                        </>
                      )}
                    </>
                  )}

                  {/* Found Course Confirmation */}
                  {foundCourse && courseLookupStep === 'confirm' && (
                    <div className="bg-[var(--ps-success)]/10 border-2 border-[var(--ps-success)]/30 rounded-xl p-4 space-y-3">
                      <div className="text-center">
                        <CheckCircle className="w-8 h-8 text-[var(--ps-success)] mx-auto" />
                        <h5 className="font-bold text-[var(--ps-success)] mt-1">{foundCourse.name}</h5>
                        <p className="text-sm text-[var(--ps-text-secondary)]">{foundCourse.location}</p>
                        <p className="text-lg font-bold text-[var(--ps-text-primary)] mt-2">Par {foundCourse.par}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setFoundCourse(null);
                            setCourseLookupStep('idle');
                          }}
                          className="flex-1 py-2 text-sm font-medium text-[var(--ps-text-secondary)] bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg"
                        >
                          Not Right
                        </button>
                        <button
                          onClick={confirmFoundCourse}
                          className="flex-1 py-2 text-sm font-bold text-[var(--ps-bg-base)] bg-[var(--ps-success)] rounded-lg hover:opacity-90"
                        >
                          That's It!
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Multi-Nine Selection for 27-hole courses */}
                  {foundCourse && courseLookupStep === 'select_nines' && foundCourse.nines && (
                    <div className="space-y-3">
                      <p className="text-sm text-[var(--ps-text-secondary)] text-center">
                        {foundCourse.nines[0]?.hole_pars?.length === 9 
                          ? "Which two nines are you playing today?"
                          : "Select your course:"}
                      </p>
                      {foundCourse.nines.map((nine, idx) => (
                        <button
                          key={`nine-${idx}`}
                          onClick={async () => {
                            if (foundCourse.nines[0]?.hole_pars?.length === 9) {
                              if (firstNineSelection === null) {
                                setFirstNineSelection(idx);
                              } else if (firstNineSelection !== idx) {
                                handleNineSelection(firstNineSelection, idx);
                              }
                            } else {
                              setFoundCourse({
                                ...foundCourse,
                                name: `${foundCourse.name} - ${nine.name}`,
                                hole_pars: nine.hole_pars,
                                par: nine.par,
                                selectedNines: { course: nine.name }
                              });
                              setCourseLookupStep('confirm');
                            }
                          }}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                            firstNineSelection === idx
                              ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                              : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-[var(--ps-primary)]/50'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-[var(--ps-text-primary)]">{nine.name}</span>
                            <span className="text-sm font-bold text-[var(--ps-primary)]">Par {nine.par}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Course Selection - Show when course already selected */}
          {selectedCourse && (
            <div className="space-y-1">
              <Label className="text-base font-bold text-[var(--ps-text-primary)]">Course*</Label>
              {loading ? (
                <div className="h-14 flex items-center">
                  <div className="dark-spinner w-6 h-6" />
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg">
                  <div>
                    <p className="font-semibold text-[var(--ps-text-primary)]">{selectedCourseData?.name || 'Selected Course'}</p>
                    <p className="text-sm text-[var(--ps-primary)]">Par {selectedCourseData?.par || 72}</p>
                  </div>
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className="px-3 py-1.5 text-sm bg-[var(--ps-primary)]/20 text-[var(--ps-primary)] rounded-lg hover:bg-[var(--ps-primary)]/30"
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          )}

          {/* New Course Dialog */}
          <Dialog open={selectedCourse === 'new' || showNewCourse} onOpenChange={(open) => {
            if (!open) {
              // Only reset selectedCourse if it's still 'new' (user cancelled)
              // Don't reset if a real course was just added (selectedCourse is a real ID)
              if (selectedCourse === 'new') {
                setSelectedCourse(null);
              }
              setShowNewCourse(false);
              setAddCourseMode('choose');
              setScannedHolePars(Array(18).fill(''));
            }
          }}>
            <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[var(--ps-text-primary)]">
                  {addCourseMode === 'choose' ? 'Add New Course' : 
                   addCourseMode === 'scan' ? 'Scan Scorecard' : 'Course Details'}
                </DialogTitle>
              </DialogHeader>

              {/* Hidden file inputs for iOS compatibility */}
              <input
                type="file"
                ref={courseCameraInputRef}
                onChange={handleCourseImageUpload}
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid="course-camera-input"
              />
              <input
                type="file"
                ref={courseGalleryInputRef}
                onChange={handleCourseImageUpload}
                accept="image/*"
                className="hidden"
                data-testid="course-gallery-input"
              />

              {/* Mode Selection */}
              {addCourseMode === 'choose' && (
                <div className="space-y-4 py-4">
                  <p className="text-sm text-[var(--ps-text-secondary)] text-center">
                    How would you like to add your course?
                  </p>
                  
                  {/* Take Photo Option */}
                  <button
                    onClick={() => courseCameraInputRef.current?.click()}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-[var(--ps-border)] hover:border-[var(--ps-primary)] bg-[var(--ps-bg-elevated)] transition-colors group"
                    data-testid="scan-scorecard-camera-btn"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-[var(--ps-primary)]/20 flex items-center justify-center group-hover:bg-[var(--ps-primary)]/30 transition-colors">
                        <Camera className="w-6 h-6 text-[var(--ps-primary)]" />
                      </div>
                      <span className="font-semibold text-[var(--ps-text-primary)]">Take Photo</span>
                      <span className="text-xs text-[var(--ps-text-muted)]">Capture scorecard with camera</span>
                    </div>
                  </button>

                  {/* Choose from Gallery Option */}
                  <button
                    onClick={() => courseGalleryInputRef.current?.click()}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-[var(--ps-border)] hover:border-[var(--ps-primary)] bg-[var(--ps-bg-elevated)] transition-colors group"
                    data-testid="scan-scorecard-gallery-btn"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                        <ImageIcon className="w-6 h-6 text-blue-500" />
                      </div>
                      <span className="font-semibold text-[var(--ps-text-primary)]">Choose Photo</span>
                      <span className="text-xs text-[var(--ps-text-muted)]">Select from photo library</span>
                    </div>
                  </button>

                  {/* Manual Option */}
                  <button
                    onClick={() => setAddCourseMode('manual')}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-[var(--ps-border)] hover:border-[var(--ps-primary)] bg-[var(--ps-bg-elevated)] transition-colors group"
                    data-testid="manual-course-btn"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-[var(--ps-bg-hover)] flex items-center justify-center group-hover:bg-[var(--ps-primary)]/20 transition-colors">
                        <FileText className="w-6 h-6 text-[var(--ps-text-secondary)] group-hover:text-[var(--ps-primary)]" />
                      </div>
                      <span className="font-semibold text-[var(--ps-text-primary)]">Enter Manually</span>
                      <span className="text-xs text-[var(--ps-text-muted)]">Type the course name and details</span>
                    </div>
                  </button>
                </div>
              )}

              {/* Scanning Loading State */}
              {scanningCourse && (
                <div className="py-8 text-center">
                  <div className="w-12 h-12 border-4 border-[var(--ps-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-[var(--ps-text-secondary)]">Scanning scorecard...</p>
                  <p className="text-xs text-[var(--ps-text-muted)] mt-1">AI is reading the hole data</p>
                </div>
              )}

              {/* Manual Entry Form */}
              {addCourseMode === 'manual' && !scanningCourse && (
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-[var(--ps-text-secondary)]">Course Name *</Label>
                    <Input
                      value={newCourseName}
                      onChange={(e) => setNewCourseName(e.target.value)}
                      placeholder="e.g., Pebble Beach"
                      className="dark-input"
                      data-testid="new-course-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[var(--ps-text-secondary)]">Total Par</Label>
                    <Input
                      type="number"
                      value={newCoursePar}
                      onChange={(e) => setNewCoursePar(e.target.value)}
                      placeholder="72"
                      className="dark-input"
                      data-testid="new-course-par"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[var(--ps-text-secondary)]">Location (optional)</Label>
                    <Input
                      value={newCourseLocation}
                      onChange={(e) => setNewCourseLocation(e.target.value)}
                      placeholder="e.g., California, USA"
                      className="dark-input"
                      data-testid="new-course-location"
                    />
                  </div>

                  {/* Show scanned hole pars if available */}
                  {scannedHolePars.some(p => p) && (
                    <div className="space-y-2 pt-2 border-t border-[var(--ps-border)]">
                      <Label className="text-[var(--ps-text-secondary)]">Hole Pars (from scan)</Label>
                      <div className="grid grid-cols-9 gap-1 text-center text-xs">
                        {scannedHolePars.slice(0, 9).map((par, i) => (
                          <div key={i} className="bg-[var(--ps-bg-elevated)] rounded p-1">
                            <div className="text-[var(--ps-text-muted)]">{i + 1}</div>
                            <div className="text-[var(--ps-text-primary)] font-bold">{par || '-'}</div>
                          </div>
                        ))}
                      </div>
                      {scannedHolePars.length > 9 && (
                        <div className="grid grid-cols-9 gap-1 text-center text-xs mt-1">
                          {scannedHolePars.slice(9, 18).map((par, i) => (
                            <div key={i + 9} className="bg-[var(--ps-bg-elevated)] rounded p-1">
                              <div className="text-[var(--ps-text-muted)]">{i + 10}</div>
                              <div className="text-[var(--ps-text-primary)] font-bold">{par || '-'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rescan options */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => courseCameraInputRef.current?.click()}
                      className="flex-1 py-2 text-sm text-[var(--ps-primary)] hover:text-[var(--ps-primary-light)] flex items-center justify-center gap-1 border border-[var(--ps-border)] rounded-lg"
                    >
                      <Camera className="w-4 h-4" /> Camera
                    </button>
                    <button
                      onClick={() => courseGalleryInputRef.current?.click()}
                      className="flex-1 py-2 text-sm text-blue-500 hover:text-blue-400 flex items-center justify-center gap-1 border border-[var(--ps-border)] rounded-lg"
                    >
                      <ImageIcon className="w-4 h-4" /> Gallery
                    </button>
                  </div>
                </div>
              )}

              {addCourseMode !== 'choose' && !scanningCourse && (
                <DialogFooter className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="dark-btn-secondary"
                    onClick={() => {
                      setAddCourseMode('choose');
                      setScannedHolePars(Array(18).fill(''));
                    }}
                  >
                    Back
                  </Button>
                  <Button onClick={handleAddCourse} className="dark-btn-primary" data-testid="save-new-course-btn">
                    Add Course
                  </Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>

            {/* Prompt to select course first if none selected */}
            {!selectedCourse && !courseMenuExpanded && (
              <div className="bg-[var(--ps-warning)]/10 border border-[var(--ps-warning)]/30 rounded-xl p-4 text-center">
                <p className="text-[var(--ps-warning)] font-medium">Please select a course first</p>
                <button
                  onClick={() => setCourseMenuExpanded(true)}
                  className="mt-2 text-[var(--ps-primary)] font-bold text-sm"
                >
                  Find Your Course →
                </button>
              </div>
            )}

            {/* Play Mode Selector - shown after course selection */}
            {selectedCourse && showPlayModeSelector && !playMode && (
              <div className="pt-4 border-t border-[var(--ps-border)]">
                <PlayModeSelector
                  selectedCourse={foundCourse || courses.find(c => c.course_id === selectedCourse)}
                  numberOfHoles={numberOfHoles}
                  onModeSelect={handlePlayModeSelect}
                  onGroupRound={handleGroupRoundMode}
                  suspended={showGroupRoundSetup}
                />
              </div>
            )}

            {/* Group Round Setup Modal */}
            <GroupRoundSetup
              isOpen={showGroupRoundSetup}
              onClose={() => setShowGroupRoundSetup(false)}
              selectedCourse={foundCourse || courses.find(c => c.course_id === selectedCourse)}
              numberOfHoles={numberOfHoles}
              startingNine={startingNine}
              currentUser={currentUser}
              onStartRound={handleStartGroupRound}
            />

            {/* Score Input Section - only show after course AND play mode are selected */}
            {selectedCourse && playMode && (
            <div className="space-y-4">
              {/* MANUAL MODE UI */}
                {/* Enter Your Score Header */}
                <div className="flex items-center justify-between pt-2 border-t border-[var(--ps-border)]">
                  <Label className="text-base font-bold text-[var(--ps-text-primary)]">Enter Your Score</Label>
                  <span className="text-xs text-[var(--ps-warning)] font-medium">Review for accuracy</span>
                </div>

                  {/* Total Score vs Hole-by-Hole Toggle */}
                  <div className="flex justify-center gap-2 p-1 bg-[var(--ps-bg-elevated)] rounded-lg">
                    <button
                      onClick={() => setManualEntryMode('total')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        manualEntryMode === 'total' ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-primary)] shadow-sm' : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                      }`}
                      data-testid="total-score-mode-btn"
                    >
                      Total Score
                    </button>
                    <button
                      onClick={() => setManualEntryMode('hole')}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        manualEntryMode === 'hole' ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-primary)] shadow-sm' : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                      }`}
                      data-testid="hole-by-hole-mode-btn"
                    >
                      Hole-by-Hole
                    </button>
                  </div>
                  
                  {/* Total Score Mode */}
                  {manualEntryMode === 'total' && (
                    <>
                      <TotalScoreEntry
                        front9={front9}
                        back9={back9}
                        numberOfHoles={numberOfHoles}
                        onFront9Change={setFront9}
                        onBack9Change={setBack9}
                      />
                      
                      {/* Scanner Option - AI-powered scorecard scanning */}
                      <ScannerUI
                        numberOfHoles={numberOfHoles}
                        onScoresChange={(scores) => {
                          // Store the hole-by-hole scores for later use
                          setScannedHoleScoresForTotal(scores);
                          
                          // Convert array of score strings to front9/back9 totals
                          const front9Scores = scores.slice(0, 9).filter(s => s).map(Number);
                          const back9Scores = scores.slice(9, 18).filter(s => s).map(Number);
                          
                          if (front9Scores.length > 0) {
                            setFront9(front9Scores.reduce((a, b) => a + b, 0).toString());
                          }
                          if (back9Scores.length > 0 && numberOfHoles === 18) {
                            setBack9(back9Scores.reduce((a, b) => a + b, 0).toString());
                          }
                        }}
                        onScanComplete={({ scores, total, front9: f9, back9: b9 }) => {
                          // Store the hole-by-hole scores for later use in sharing
                          setScannedHoleScoresForTotal(scores || []);
                          
                          if (f9) setFront9(f9.toString());
                          if (b9) setBack9(b9.toString());
                          toast.success('Scores imported from scan!');
                        }}
                      />
                    </>
                  )}

                  {/* Hole-by-Hole Mode - Using Unified ScoreEntryForm Component */}
                  {manualEntryMode === 'hole' && (
                    <>
                      {/* Input Method Toggle: Voice | Manual | Scan */}
                      <div className="flex justify-center gap-1 p-1 bg-[var(--ps-bg-elevated)] rounded-lg">
                        <button
                          onClick={() => setHoleEntryMethod('voice')}
                          className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            holeEntryMethod === 'voice' ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-primary)] shadow-sm' : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                          }`}
                        >
                          Voice
                        </button>
                        <span className="text-[var(--ps-text-muted)] self-center">|</span>
                        <button
                          onClick={() => setHoleEntryMethod('manual')}
                          className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            holeEntryMethod === 'manual' ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-primary)] shadow-sm' : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                          }`}
                        >
                          Manual
                        </button>
                        <span className="text-[var(--ps-text-muted)] self-center">|</span>
                        <button
                          onClick={() => setHoleEntryMethod('scan')}
                          className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            holeEntryMethod === 'scan' ? 'bg-[var(--ps-bg-surface)] text-[var(--ps-primary)] shadow-sm' : 'text-[var(--ps-text-secondary)] hover:text-[var(--ps-text-primary)]'
                          }`}
                        >
                          📷 Scan
                        </button>
                      </div>

                      {/* Tee Selector - Only show if course has tees with yardages */}
                      {availableTees.length > 0 && (
                        <div className="flex items-center justify-center gap-2 py-2">
                          <span className="text-xs text-[var(--ps-text-secondary)]">Tee:</span>
                          <div className="flex gap-1 bg-[var(--ps-bg-elevated)] rounded-lg p-0.5">
                            {availableTees.filter(t => t.hole_yardages && t.hole_yardages.length > 0).map((tee, idx) => (
                              <button
                                key={idx}
                                onClick={() => {
                                  setSelectedTee(tee);
                                  setHoleYardages(tee.hole_yardages || []);
                                }}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  selectedTee?.name === tee.name 
                                    ? 'bg-blue-500 text-white shadow-sm' 
                                    : 'text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-surface)]'
                                }`}
                                title={`${tee.total_yards?.toLocaleString()} yards | Rating: ${tee.course_rating} | Slope: ${tee.slope_rating}`}
                              >
                                {tee.name}
                              </button>
                            ))}
                          </div>
                          {selectedTee && (
                            <span className="text-xs text-blue-500 font-medium">
                              {selectedTee.total_yards?.toLocaleString()}y
                            </span>
                          )}
                        </div>
                      )}

                      {/* Starting Nine Selector - Only show for 9-hole rounds on 18-hole courses */}
                      {numberOfHoles === 9 && (
                        <div className="flex items-center justify-center gap-2 py-2">
                          <span className="text-xs text-[var(--ps-text-secondary)]">Starting:</span>
                          <div className="flex gap-1 bg-[var(--ps-bg-elevated)] rounded-lg p-0.5">
                            <button
                              onClick={() => setStartingNine('front')}
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                startingNine === 'front' 
                                  ? 'bg-[var(--ps-primary)] text-white shadow-sm' 
                                  : 'text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-surface)]'
                              }`}
                            >
                              Front 9
                            </button>
                            <button
                              onClick={() => setStartingNine('back')}
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                startingNine === 'back' 
                                  ? 'bg-[var(--ps-primary)] text-white shadow-sm' 
                                  : 'text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-surface)]'
                              }`}
                            >
                              Back 9
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Scan Mode - AI Scorecard Scanner */}
                      {holeEntryMethod === 'scan' && (
                        <div className="space-y-3">
                          <ScannerUI
                            numberOfHoles={numberOfHoles}
                            onScoresChange={(scores) => {
                              // Populate hole-by-hole scores from scan
                              const paddedScores = [...scores];
                              while (paddedScores.length < 18) paddedScores.push('');
                              setManualHoleScores(paddedScores.map(s => s?.toString() || ''));
                            }}
                            onScanComplete={({ scores, total, front9: f9, back9: b9 }) => {
                              // Populate hole-by-hole scores from scan
                              const paddedScores = [...(scores || [])];
                              while (paddedScores.length < 18) paddedScores.push('');
                              setManualHoleScores(paddedScores.map(s => s?.toString() || ''));
                              setHoleEntryMethod('manual'); // Switch to manual for editing
                              toast.success('Scores imported! You can now edit hole-by-hole.');
                            }}
                          />
                          <p className="text-xs text-[var(--ps-text-muted)] text-center">
                            Take a photo of your scorecard and we'll extract the scores automatically.
                            You can edit them after scanning.
                          </p>
                        </div>
                      )}

                      {/* Voice & Manual Mode - ScoreEntryForm */}
                      {(holeEntryMethod === 'voice' || holeEntryMethod === 'manual') && (
                        <ScoreEntryForm
                          scoreReactions={holeEntryMethod === 'manual' ? { enabled: scoreAnimationPreference.enabled, context: `${user?.user_id}|${date.toISOString()}` } : undefined}
                          courseId={selectedCourse || foundCourse?.course_id}
                          numberOfHoles={numberOfHoles}
                          onNumberOfHolesChange={setNumberOfHoles}
                          initialScores={manualHoleScores}
                          onScoresChange={setManualHoleScores}
                          showHolesSelector={false}
                          showScanOption={false}
                          autoSave={true}
                          autoSaveEndpoint={`${API}/scores/draft`}
                          autoSavePayload={draftPayload}
                          showTotalDisplay={true}
                          holePars={foundCourse?.hole_pars || (selectedCourse ? (courses.find(c => c.course_id === selectedCourse)?.hole_pars || []) : [])}
                          holeYardages={holeYardages}
                          holeHandicaps={holeHandicaps}
                          showYardages={holeYardages.length > 0}
                          courseName={foundCourse?.name || (selectedCourse ? (courses.find(c => c.course_id === selectedCourse)?.name || '') : '')}
                          showHoleInfoCards={true}
                          submitButtonText="Save Score"
                          showVoiceOption={holeEntryMethod === 'voice'}
                          defaultInputMode={holeEntryMethod === 'voice' ? 'voice' : 'manual'}
                          startingNine={startingNine}
                          getActualHoleNumber={getActualHoleNumber}
                          onSave={async ({ scores, total, front9: f9, back9: b9, holes }) => {
                            if (!selectedCourse) {
                              toast.error("Please select a course");
                              return;
                            }
                            const course = courses.find(c => c.course_id === selectedCourse);
                        setSubmitting(true);
                        try {
                          const scorePayload = {
                            course_id: selectedCourse,
                            course_name: course.name,
                            date: format(new Date(), 'yyyy-MM-dd'),
                            total_score: total,
                            ...roundParPayload(course, holes, startingNine),
                            front_9: f9,
                            back_9: b9,
                            hole_scores: scores.map(Number),
                            holes_played: holes,
                            notes: notes.trim() || `${holes}-hole round: ${scores.join(', ')}`
                          };
                          
                          const response = await axios.post(`${API}/scores`, scorePayload);
                          
                          // Delete draft after successful save
                          try {
                            await axios.delete(`${API}${scoreDraftPath(draftId)}`);
                          } catch (e) { /* ignore */ }
                          
                          toast.success("Score saved!");
                          
                          // Prepare score data for potential sharing
                          // Parse scores to integers, filtering out empty values
                          const parsedHoleScores = scores.map((score, i) => {
                            const scoreNum = parseInt(score);
                            return { 
                              hole: i + 1, 
                              score: isNaN(scoreNum) ? null : scoreNum,
                              par: course.hole_pars?.[i] || course.holes?.[i]?.par || 4
                            };
                          });
                          
                          setSavedScoreData({
                            ...scorePayload,
                            score_id: response.data?.score_id,
                            hole_scores: parsedHoleScores,
                            course_pars: course.hole_pars || course.holes || []
                          });
                          
                          // Show quick post-save options panel
                          setShowPostSaveOptions(true);
                        } catch (error) {
                          toast.error("Failed to save score");
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                      disabled={submitting}
                      saveDisabled={!selectedCourse}
                    />
                      )}
                      
                  {/* Clear Draft for Hole-by-Hole mode */}
                  <div className="text-center pt-2">
                    <button
                      onClick={clearDraft}
                      className="text-red-500 text-sm hover:text-red-700 transition-colors flex items-center justify-center gap-1 mx-auto"
                      data-testid="clear-draft-hole-btn"
                    >
                      <Trash2 className="w-3 h-3" />
                      Clear Draft
                    </button>
                  </div>
                </>
              )}

              {/* Score vs Par - Only show for total mode */}
              {manualEntryMode === 'total' && selectedCourseData && totalScore && (
                <div className="text-center p-3 bg-stone-50 rounded-lg">
                  <span className="text-sm text-stone-500">vs Par {selectedCourseData.par}: </span>
                  <span className={`text-lg font-mono font-bold ${
                    totalScore - selectedCourseData.par < 0 ? 'text-green-600' :
                    totalScore - selectedCourseData.par === 0 ? 'text-blue-600' :
                    'text-amber-600'
                  }`}>
                    {totalScore - selectedCourseData.par >= 0 ? '+' : ''}{totalScore - selectedCourseData.par}
                  </span>
                </div>
              )}

            {/* Submit - Only for Total Score Mode */}
            {manualEntryMode === 'total' && (
              <div className="text-center pt-4 border-t border-[var(--ps-border)] space-y-3">
                <button
                  onClick={handleSubmit}
                  disabled={isTotalScoreSaveDisabled({ submitting, selectedCourse, front9, back9, holesPlayed: numberOfHoles })}
                  className="text-[var(--ps-primary)] font-bold hover:opacity-80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
                  data-testid="save-score-btn"
                >
                  {submitting ? (
                    <div className="dark-spinner w-4 h-4" />
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4" />
                      Save Score
                    </>
                  )}
                </button>
                <button
                  onClick={clearDraft}
                  className="text-[var(--ps-error)] text-sm hover:opacity-80 transition-colors flex items-center justify-center gap-1 mx-auto"
                  data-testid="clear-draft-btn"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear Draft
                </button>
              </div>
            )}
            </div>
          )}
        </div>
      </div>

      {/* Post-Save Options Panel - Quick choice after saving */}
      {showPostSaveOptions && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--ps-bg-card)] rounded-2xl w-full max-w-sm shadow-2xl border border-[var(--ps-border)] animate-fade-in overflow-hidden">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-[var(--ps-primary)] to-[var(--ps-primary-dark)] p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Round Saved!</h2>
              {savedScoreData && (
                <p className="text-white/80 text-sm mt-1">
                  {savedScoreData.total_score} at {savedScoreData.course_name}
                </p>
              )}
            </div>
            
            {/* Options */}
            <div className="p-5 space-y-3">
              <p className="text-center text-[var(--ps-text-secondary)] text-sm mb-4">
                What would you like to do?
              </p>
              
              {/* Share to Feed Option */}
              <button
                onClick={() => {
                  setShowPostSaveOptions(false);
                  setShowShareScorecard(true);
                }}
                className="w-full flex items-center gap-4 p-4 bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] rounded-xl border border-[var(--ps-border)] transition-all group"
                data-testid="post-round-btn"
              >
                <div className="w-12 h-12 rounded-full bg-[var(--ps-primary)]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Share2 className="w-6 h-6 text-[var(--ps-primary)]" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-[var(--ps-text-primary)]">Post Round to Feed</p>
                  <p className="text-xs text-[var(--ps-text-secondary)]">Create a shareable scorecard image</p>
                </div>
                <ChevronDown className="w-5 h-5 text-[var(--ps-text-muted)] -rotate-90" />
              </button>
              
              {/* Just Continue Option */}
              <button
                onClick={() => {
                  setShowPostSaveOptions(false);
                  navigate('/history');
                }}
                className="w-full flex items-center gap-4 p-4 bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] rounded-xl border border-[var(--ps-border)] transition-all group"
                data-testid="done-btn"
              >
                <div className="w-12 h-12 rounded-full bg-[var(--ps-text-muted)]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Check className="w-6 h-6 text-[var(--ps-text-secondary)]" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-[var(--ps-text-primary)]">Done</p>
                  <p className="text-xs text-[var(--ps-text-secondary)]">View your rounds history</p>
                </div>
                <ChevronDown className="w-5 h-5 text-[var(--ps-text-muted)] -rotate-90" />
              </button>
            </div>
            
            {/* Footer hint */}
            <div className="px-5 pb-5">
              <p className="text-center text-xs text-[var(--ps-text-muted)]">
                You can share any round later from your History
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Shareable Scorecard Modal */}
      <ShareableScorecard
        isOpen={showShareScorecard}
        score={savedScoreData}
        user={{ ...user, handicap: latestHandicap }}
        course={courses.find(c => c.course_id === selectedCourse)}
        onClose={handleCloseShareModal}
        onShareToFeed={handleShareToFeed}
        onEdit={(score) => {
          // Navigate to history page with edit intent
          navigate(`/history?edit=${score.score_id}`);
        }}
      />
    </div>
  );
};


export default AddScorePage;
